import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  FlatList,
  Modal,
  TouchableOpacity,
  Platform,
  Dimensions,
  Image,
  ActivityIndicator,
  TextInput,
  RefreshControl,
  Switch,
} from 'react-native';
import { overlayBackdropStyle, overlayCardLook } from './OverlayPanel';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from './DirectionalIcon';
import { colors, spacing, typography } from '../theme';
import { apiService } from '../services/api';
import { getChannelIcon, getChannelColor } from '../utils/channelIcons';
import { decodeBase64Url } from '../utils/token-utils';
import { socketService } from '../services/socket.service';
import { UserAssignmentModal } from './UserAssignmentModal';
import { BulkUploadProgressIndicator } from './BulkUploadProgressIndicator';
import { ContactSortFilterModal, HAS_VALUE_SENTINEL, NO_VALUE_SENTINEL } from './ContactSortFilterModal';
import { ContactAnalyticalFiltersModal, ContactAnalyticalFilters, countActiveAnalyticalFilters } from './ContactAnalyticalFiltersModal';
import { MapLocationPicker } from './MapLocationPicker';
import { useAppStore } from '../store/appStore';
import { getImageUrlSync, getApiBaseURL } from '../utils/imageProxy';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useAppContext } from '../contexts/AppContext';
import { usePermissions } from '../hooks/usePermissions';
import * as Location from 'expo-location';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useContactsFilterField, useResetContactsFilters, ContactsFilterScopeContext } from '../store/contactsFilterStore';
import type { AssignmentFilterTarget } from '../store/contactsFilterStore';
import { withAlpha } from '../theme';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { firstText } from '../utils/contactIdentifiers';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobileBrowser = Platform.OS === 'web' && (screenWidth < 768);

/** Anchors a dropdown menu below+aligned-to its trigger button, based on a
 * measureInWindow rect — window-relative in both LTR and RTL, matching a
 * Modal's coordinate space (Modal portals to the document root on web, so
 * position:absolute inside it is relative to the window, not whatever
 * ancestor happened to be positioned in the underlying screen).
 *
 * `rect.x` from measureInWindow is ALWAYS the gap from the window's LEFT
 * edge, never the right, regardless of locale — reusing it directly as a
 * `right` value in RTL anchored the menu the same raw distance from the
 * WRONG edge (looked identical to the LTR position, just mirrored wrong).
 * The correct `right` gap is windowWidth - x - width. */
/** Converts a touch event's DOCUMENT-relative pageX/pageY (what
 * event.nativeEvent reports on web — includes page scroll, per the DOM
 * spec) into WINDOW/viewport-relative coordinates, which is what a Modal's
 * position:absolute content actually needs (see menuAnchorStyle's doc
 * comment above — the Modal portals to document.body with
 * position:'fixed', i.e. viewport-anchored, on web). Without this
 * conversion, any popover anchored via raw pageX/pageY drifts further from
 * the tapped row the more the page has been scrolled — on native this is a
 * no-op since pageX/pageY there are already the value RN Modal expects. */
const toWindowRelative = (pageX: number, pageY: number): { x: number; y: number } => {
  if (!isWeb || typeof window === 'undefined') return { x: pageX, y: pageY };
  return { x: pageX - window.scrollX, y: pageY - window.scrollY };
};

const menuAnchorStyle = (
  rect: { x: number; y: number; width: number; height: number },
  isRTL: boolean,
  menuWidth: number
): { top: number; left?: number; right?: number } => {
  const top = rect.y + rect.height + 6;
  // Both keys always present (one real, one explicitly undefined) — the
  // caller merges this into a style ARRAY on top of menuModalContainer's
  // base `right: spacing.lg`; an ABSENT key in a later array entry doesn't
  // override an earlier one during RN Web's style flattening (only an
  // explicit `undefined` does), so leaving out the inactive side here would
  // let the base style's right offset silently keep winning in LTR.
  // Confirmed real bug: this branch only clamped the LOWER bound of `right`
  // (never overflow past the left edge) and never used `menuWidth` at all —
  // unlike the LTR branch below, which clamps BOTH ends. A trigger sitting
  // near the left edge of a narrow container (the compact right pane is only
  // a few hundred px wide) makes `screenWidth - (rect.x + rect.width)` large,
  // and since the menu's actual left edge is `screenWidth - right - menuWidth`,
  // an unclamped `right` that large pushes the menu's left edge negative —
  // off the left side of the window. Clamp the UPPER bound too, symmetric to
  // the LTR branch's `Math.min(rect.x, screenWidth - menuWidth)`: `right`
  // must never exceed `screenWidth - menuWidth`, which keeps the menu's left
  // edge >= 0.
  return isRTL
    ? { top, left: undefined, right: Math.max(spacing.sm, Math.min(screenWidth - (rect.x + rect.width), screenWidth - menuWidth)) }
    : { top, right: undefined, left: Math.max(spacing.sm, Math.min(rect.x, screenWidth - menuWidth)) };
};

// Shared cache for the 4 filter-metadata fetches below (tags, data lists,
// location cities, custom field keys). Module-level (outside the component)
// so it survives across ContactsList remounts — every top-level navigation
// in this app remounts the whole screen (and, now, this component wherever
// it lives in a pane), which previously re-fired all 4 of these on every
// single page view even though this data barely ever changes mid-session.
// Confirmed real cost: a request-log capture during "page loads very slowly"
// showed these firing 2-4x in a short window purely from the contacts pane
// remounting across normal navigation. A short in-memory TTL (not a store,
// no persistence needed) plus in-flight de-duping fixes both that and the
// narrower case of this same component's own 3 independent loadContacts()-
// triggering effects (mount/search/filters) each racing to fetch tags on
// first mount (see loadContactsRequestIdRef's doc comment for why those 3
// calls exist in the first place).
const FILTER_METADATA_CACHE_TTL_MS = 5 * 60 * 1000;

function createCachedFetcher<T>(fetchFn: () => Promise<{ statusCode: number; data: T | null }>) {
  let cache: { data: T; ts: number } | null = null;
  let inFlight: Promise<T | null> | null = null;
  return (forceRefresh = false): Promise<T | null> => {
    if (!forceRefresh && cache && Date.now() - cache.ts < FILTER_METADATA_CACHE_TTL_MS) {
      return Promise.resolve(cache.data);
    }
    if (!forceRefresh && inFlight) {
      return inFlight;
    }
    inFlight = fetchFn()
      .then(res => {
        if (res.statusCode === 1000 && Array.isArray(res.data)) {
          cache = { data: res.data, ts: Date.now() };
          return res.data;
        }
        return null;
      })
      .catch(() => null)
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  };
}

const fetchTagsCached = createCachedFetcher<string[]>(() => apiService.getContactTags());
const fetchContactDataListsCached = createCachedFetcher<{ _id: string; name: string }[]>(() => apiService.getDataLists({ systemModule: 'contacts', limit: 100 }));
const fetchLocationCitiesCached = createCachedFetcher<string[]>(() => apiService.getContactLocationCities());
const fetchCustomFieldKeysCached = createCachedFetcher<string[]>(() => apiService.getContactCustomFieldKeys());

export interface Contact {
  id: string;
  name: string;
  // Widened from the original 'whatsapp' | 'facebook-messenger' | 'telegram'
  // | 'email', which didn't include this platform's Phone or X (Twitter)
  // channels at all — a contact whose only/primary linked channel was
  // either of those got silently relabeled 'whatsapp' below (see
  // primaryChannel) and merged into the WhatsApp section instead of
  // getting its own group, or its own group failing to render at all.
  channel: 'whatsapp' | 'facebook-messenger' | 'x-twitter' | 'phone' | 'telegram' | 'email';
  phoneNumber?: string;
  facebookId?: string;
  email?: string;
  phone?: string;
  profilePictureUrl?: string;
  isOnline?: boolean;
  lastSeen?: string;
  messageCount?: number;
  source?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  tags?: string[]; // Contact tags
  linkedChannels?: string[]; // Channels linked to this parent contact (identity links, may include channels never actually messaged)
  // Channels this contact has ACTUALLY exchanged messages on (derived from
  // communicationHistory's messageCount, not just an identity link) — the
  // Channel filter matches against this, not linkedChannels, per explicit
  // correction: "channel communicated filters (not channel contacts)".
  communicatedChannels?: string[];
  parentContact?: any; // Full parent contact data
  assignment?: {
    _id?: string;
    assignedTo?: {
      _id?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
    };
    departmentId?: {
      _id?: string;
      name?: string;
      description?: string;
    };
    assignedBy?: string;
    assignmentType?: string;
    status?: string;
    createdAt?: string;
  } | null;
  assignments?: Array<{
    _id?: string;
    assignedTo?: {
      _id?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
    };
    departmentId?: {
      _id?: string;
      name?: string;
      description?: string;
    };
    assignedBy?: string;
    assignmentType?: string;
    status?: string;
    createdAt?: string;
  }>;
}

// Floor for the free-typed "Page size" control —
// genuinely no preset list and no upper cap; this just keeps 0/negative/blank
// from silently breaking pagination (limit=0 would request nothing forever).
// Persisted per user+business (see contactsFilterStore's pageSize field),
// same as every other filter there.
const PAGE_SIZE_MIN = 1;

// Module-level (not component-scoped) since these are pure functions of their
// argument only — no need to recreate them, or wrap them in useCallback, every render.
// Delegates to the shared channel icon/color map (utils/channelIcons.ts,
// the same one InboxScreen/ReportsScreen use) instead of this file's own
// incomplete copy, which only ever knew about whatsapp/facebook-messenger/
// telegram/email — missing Phone and X (Twitter) entirely, two channels
// this platform actually has. Any contact whose ONLY linked channel was
// Phone or X never got a provider group at all (see the `providers` memo
// below), so on a business with mixed channels, only WhatsApp/Facebook
// contacts ever appeared in the list — the rest silently vanished.
const getProviderIcon = (provider: string): any => getChannelIcon(provider);
const getProviderColor = (provider: string): string => getChannelColor(provider);

// Accepts `t` since this needs translated names but stays a pure function of
// its arguments (module-level, not component-scoped) like its siblings above.
const getProviderName = (provider: string, t: (key: any) => string): string => {
  switch (provider) {
    case 'whatsapp':
      return t('contacts.provider.whatsapp');
    case 'facebook-messenger':
      return t('contacts.provider.messenger');
    case 'x-twitter':
      return t('contacts.provider.xTwitter');
    case 'phone':
      return t('contacts.provider.phone');
    case 'telegram':
      return t('contacts.provider.telegram');
    case 'email':
      return t('contacts.provider.email');
    default:
      return provider;
  }
};

interface ContactRowProps {
  contact: Contact;
  isSelected: boolean;
  /** True when this contact's profile/conversation is the one currently open
   * elsewhere (the screen's middle/right pane) — a separate concept from
   * isSelected (bulk-action checkbox), a contact can be both, either, or
   * neither at once. */
  isActive: boolean;
  selectionMode: boolean;
  canManageContactAssignments: boolean;
  apiBaseURL: string | null;
  compact: boolean;
  onPress: (contact: Contact) => void;
  onAssignmentPress: (contact: Contact, event?: any) => void;
  onTagsPress: (contact: Contact, event?: any) => void;
  getAssignmentLabels: (contact: Contact) => string[];
  formatAssignmentLabel: (labels: string[]) => string;
}

// Inbox mode: the contact's latest conversation (see attachInboxConversations).
const INBOX_CHANNEL_ICONS: Record<string, { icon: string; color: string }> = {
  whatsapp: { icon: 'logo-whatsapp', color: '#25D366' },
  'facebook-messenger': { icon: 'logo-facebook', color: '#1877F2' },
  facebook: { icon: 'logo-facebook', color: '#1877F2' },
  instagram: { icon: 'logo-instagram', color: '#E4405F' },
  telegram: { icon: 'paper-plane', color: '#0088cc' },
  'x-twitter': { icon: 'logo-twitter', color: '#000000' },
  phone: { icon: 'call', color: '#6366F1' },
  email: { icon: 'mail', color: '#EA4335' },
};

const formatInboxTime = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return date.toLocaleDateString([], { day: 'numeric', month: 'short', ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
};

/** The 24-hour reply window from the contact's last message: same thresholds as the inbox filter. */
const inboxWindowStatus = (conversation: any): 'active' | 'near-expiry' | 'expired' => {
  const lastInbound = conversation?.lastInboundMessageAt || conversation?.contactLastMessageAt;
  if (!lastInbound) return 'expired';
  const hours = (Date.now() - new Date(lastInbound).getTime()) / (60 * 60 * 1000);
  return hours >= 24 ? 'expired' : hours >= 22 ? 'near-expiry' : 'active';
};

const isVisibleAssignment = (assignment?: { status?: string | null }) =>
  !assignment?.status || assignment.status === 'active' || assignment.status === 'inProgress';

// Extracted and memoized so a state change elsewhere in the list (opening a dropdown,
// toggling a filter) doesn't force React to re-render and re-diff every contact row —
// only rows whose own props actually changed re-render. Paired with FlatList (see
// below) instead of the old ScrollView + .map(), which mounted every row regardless
// of scroll position or whether anything about it changed.
const ContactRow = React.memo<ContactRowProps>(({
  contact,
  isSelected,
  isActive,
  selectionMode,
  canManageContactAssignments,
  apiBaseURL,
  compact,
  onPress,
  onAssignmentPress,
  onTagsPress,
  getAssignmentLabels,
  formatAssignmentLabel,
}) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const labels = getAssignmentLabels(contact);
  const hasAssignments = labels.length > 0;
  const activeAssignments = (contact.assignments && contact.assignments.length > 0
    ? contact.assignments
    : contact.assignment
    ? [contact.assignment]
    : []).filter(isVisibleAssignment);
  const hasDepartment = activeAssignments.some(a => a?.departmentId?.name);
  const { t } = useLocale();
  const inboxConversation = (contact as any).inboxConversation;
  const inboxChannel = inboxConversation ? INBOX_CHANNEL_ICONS[inboxConversation.channel] : undefined;
  const inboxWindow = inboxConversation && inboxConversation.channel !== 'phone' ? inboxWindowStatus(inboxConversation) : 'active';
  const inboxPreview = inboxConversation
    ? (typeof inboxConversation.lastMessage === 'string' && /^\s*[{[]/.test(inboxConversation.lastMessage)
      ? t('inbox.templateMessageSent')
      : inboxConversation.lastMessage || t('inbox.noMessagesYet'))
    : '';

  return (
    <TouchableOpacity
      style={[styles.contactItem, { flexDirection: rtl.rowDirection }, isActive && styles.contactItemActive]}
      onPress={() => onPress(contact)}
      activeOpacity={0.7}
    >
      {selectionMode && (
        <View style={styles.checkboxWrapper}>
          <View style={[styles.checkbox, isSelected && styles.checkboxChecked]}>
            {isSelected && <Ionicons name="checkmark" size={14} color={colors.white} />}
          </View>
        </View>
      )}
      <View style={styles.contactAvatarContainer}>
        {contact.profilePictureUrl ? (
          <Image
            source={{ uri: getImageUrlSync(contact.profilePictureUrl, apiBaseURL) }}
            style={styles.contactAvatar}
            onError={(e) => {
              console.error('❌ [ContactsList] Failed to load contact avatar:', contact.profilePictureUrl, e.nativeEvent.error);
            }}
          />
        ) : (
          <View style={[styles.contactAvatar, { backgroundColor: withAlpha(colors.primary, 0.13) }]}>
            <Text style={styles.contactAvatarText}>
              {contact.name.split(' ').map(n => n.charAt(0)).join('').substring(0, 2)}
            </Text>
          </View>
        )}
        <View style={[styles.statusDot, { backgroundColor: contact.isOnline ? colors.success : colors.text.secondary }]} />
      </View>

      <View style={styles.contactInfo}>
        <View style={[styles.inboxNameRow, { flexDirection: rtl.rowDirection }]}>
          <Text style={[styles.contactName, styles.inboxNameText, { textAlign: rtl.textStart }]} numberOfLines={1}>
            {contact.name}
          </Text>
          {inboxWindow === 'expired' && <Ionicons name="time" size={12} color="#FF6B6B" />}
          {inboxWindow === 'near-expiry' && <Ionicons name="warning" size={12} color="#FFA726" />}
        </View>
        {!!inboxConversation && (
          <View style={[styles.inboxPreviewRow, { flexDirection: rtl.rowDirection }]}>
            {!!inboxChannel && <Ionicons name={inboxChannel.icon as any} size={12} color={inboxChannel.color} />}
            <Text style={[styles.inboxPreviewText, inboxConversation.unreadCount > 0 && styles.inboxPreviewTextUnread, { textAlign: rtl.textStart }]} numberOfLines={1}>
              {inboxPreview}
            </Text>
          </View>
        )}

        {canManageContactAssignments && (
          <TouchableOpacity
            style={styles.assignmentBadgeWrapper}
            onPress={(e) => onAssignmentPress(contact, e)}
            activeOpacity={0.7}
          >
            <View style={[styles.assignmentBadge, { flexDirection: rtl.rowDirection }, !hasAssignments && styles.assignmentBadgeUnassigned]}>
              <Ionicons
                name={hasDepartment ? 'people' : 'person'}
                size={12}
                color={hasAssignments ? colors.primary : colors.text.disabled}
              />
              <Text style={[styles.assignmentText, !hasAssignments && styles.assignmentTextUnassigned]}>
                {formatAssignmentLabel(labels)}
              </Text>
            </View>
          </TouchableOpacity>
        )}
      </View>

      {/* Single "view contact" affordance — the whole row is already
          clickable (onPress above), this is just a visual cue, matching the
          explicit ask to replace the old per-row call/chat action icons. */}
      {inboxConversation ? (
        <View style={styles.inboxMeta}>
          <Text style={styles.inboxTime}>{formatInboxTime(inboxConversation.lastMessageAt)}</Text>
          {inboxConversation.unreadCount > 0 && (
            <View style={styles.inboxUnreadBadge}>
              <Text style={styles.inboxUnreadText}>{inboxConversation.unreadCount > 99 ? '99+' : inboxConversation.unreadCount}</Text>
            </View>
          )}
        </View>
      ) : (
        <DirectionalIcon name="chevron-forward" size={16} color={colors.primary} style={styles.viewContactChevron} />
      )}
    </TouchableOpacity>
  );
});
ContactRow.displayName = 'ContactRow';

interface ContactsListProps {
  showHeader?: boolean;
  /** Independent of showHeader — hides just the left title/icon/count group
   * within the header row while keeping the filter/selection buttons on the
   * right. Set false when the parent screen already renders its own title
   * (e.g. ContactsListScreen's FormHeader), so this component's header row
   * becomes filter/selection controls only instead of a second stacked
   * title bar. Defaults true (unchanged behavior for other callers). */
  showTitleBar?: boolean;
  showSearch?: boolean;
  showFilters?: boolean;
  /** Rendered in the header's left slot instead of the title block, only
   * when showTitleBar is false — lets a parent that already hides the title
   * (because it has its own search bar, e.g. ContactsListScreen) fold that
   * search bar into this component's own header row instead of stacking it
   * above as a separate full-width row that eats into the list's space. */
  headerLeftContent?: React.ReactNode;
  onContactPress?: (contact: Contact) => void;
  /** Whether the phone channel is connected for this business. Currently
   * accepted and threaded through by every caller but unused internally —
   * the per-row call icon this used to gate was removed in favor of a
   * single "view contact" affordance; kept in the prop contract rather than
   * touching every call site for a no-op removal. */
  hasPhoneService?: boolean;
  /** Id of the contact currently open elsewhere (a screen's middle/right
   * pane) — shows a visual "active" mark on that row. */
  activeContactId?: string;
  onContactsUpdated?: (contacts: Contact[]) => void;
  containerStyle?: any;
  searchQuery?: string;
  refreshTrigger?: number; // Trigger refresh when this value changes
  /** Multi-select for bulk WhatsApp: when true, show checkboxes and Select all */
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (contact: Contact) => void;
  onSelectAllFiltered?: (contacts: Contact[]) => void;
  onExitSelectionMode?: () => void;
  onEnterSelectionMode?: () => void;
  scrollContentPaddingBottom?: number;
  /**
   * Narrow-width layout (e.g. the ~350px RightPane): collapses the header's
   * separate filter icon buttons into a single overflow menu, uses a denser
   * single-line contact row, and positions dropdown menus with viewport-boundary
   * awareness instead of a fixed right-edge offset that can clip/crowd in a
   * narrow container. Without this, a component built for full-width screens
   * was simply squeezed into the pane as-is.
   */
  compact?: boolean;
  /**
   * Pre-selects the existing "filter by Data List" dropdown (see
   * includeDataListIds state below) to this list on mount — used when this
   * screen is reached via a system Contacts Data List's scoped mode (see
   * DataListRecordsScreen.tsx's redirect), so the list opens already scoped
   * instead of requiring the user to manually pick it from the dropdown.
   * The user can still change/clear it via the dropdown afterward like any
   * other filter — this only seeds the initial value.
   */
  initialDataListFilter?: { id: string; name: string };
  /** Reports this list's current filter/sort params (search, tags, data
   * lists, location, sort, custom fields) any time one changes — lets the
   * parent screen's "export all contacts" apply the SAME filters
   * server-side rather than exporting every contact in the business
   * regardless of what's on screen. See ContactsFilterParams below for the
   * shape (matches apiService.getParentContacts' filter-only options). */
  onFiltersChanged?: (params: ContactsFilterParams) => void;
  /** 'inbox': the Inbox's list — only contacts something was said to or by, same order and
   * filters as Contacts (remembered separately) plus the inbox status filter, each row showing its
   * latest conversation (`contact.inboxConversation`, a unified-conversation row). */
  mode?: 'contacts' | 'inbox';
}

/** The filter/sort subset of apiService.getParentContacts' options —
 * everything except limit/offset/ids, since those are pagination/selection
 * concerns loadContacts and the export flow each handle their own way. */
export type ContactsFilterParams = Omit<NonNullable<Parameters<typeof apiService.getParentContacts>[0]>, 'limit' | 'offset'>;

const ContactsListBody: React.FC<ContactsListProps> = ({
  showHeader = true,
  showTitleBar = true,
  showSearch = false,
  headerLeftContent,
  showFilters = true,
  onContactPress,
  hasPhoneService = false,
  activeContactId,
  onContactsUpdated,
  containerStyle,
  searchQuery: externalSearchQuery,
  refreshTrigger,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
  onSelectAllFiltered,
  onExitSelectionMode,
  onEnterSelectionMode,
  scrollContentPaddingBottom = 0,
  compact = false,
  initialDataListFilter,
  onFiltersChanged,
  mode = 'contacts',
}) => {
  const isInbox = mode === 'inbox';
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const { navigateTo, routeParams } = useAppContext();
  const rtl = useRTLStyles();
  const countries = useAppStore((state) => state.countries);
  const contactsRefreshToken = useAppStore((state) => state.contactsRefreshToken);
  const lastContactsRefreshTokenRef = useRef(contactsRefreshToken);
  const selectedSet = selectedIds ?? new Set<string>();
  // Shared (screen <-> right pane) and persisted per user+business — see
  // contactsFilterStore.ts's own doc comment.
  const [selectedProvider, setSelectedProvider] = useContactsFilterField('selectedProvider');
  const [inboxStatus, setInboxStatus] = useContactsFilterField('inboxStatus');
  const [showChannelMenu, setShowChannelMenu] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [showAssignmentMenu, setShowAssignmentMenu] = useState(false);
  const [includeAssignmentFilters, setIncludeAssignmentFilters] = useContactsFilterField('includeAssignmentFilters');
  const [excludeAssignmentFilters, setExcludeAssignmentFilters] = useContactsFilterField('excludeAssignmentFilters');
  const [assignmentUsers, setAssignmentUsers] = useState<Array<{ _id: string; firstName?: string; lastName?: string; email?: string; role?: string }>>([]);
  const [assignmentDepartments, setAssignmentDepartments] = useState<Array<{ _id: string; name: string; description?: string }>>([]);
  const [assignmentUsersLoading, setAssignmentUsersLoading] = useState(false);
  const [assignmentDepartmentsLoading, setAssignmentDepartmentsLoading] = useState(false);
  const assignmentButtonRef = useRef<View | null>(null);
  const [assignmentMenuPosition, setAssignmentMenuPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  // Non-compact tag/data-list filter menus previously used a hardcoded
  // { top: 60, right: spacing.lg } overlay — always right-anchored
  // regardless of RTL, which put the dropdown on the wrong side (and
  // sometimes off-button) in Arabic. Measured like assignmentMenuPosition
  // above instead, so positioning is based on the actual trigger button's
  // location rather than an assumed LTR layout.
  const tagButtonRef = useRef<View | null>(null);
  const [tagMenuPosition, setTagMenuPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const dataListButtonRef = useRef<View | null>(null);
  const [dataListMenuPosition, setDataListMenuPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const channelButtonRef = useRef<View | null>(null);
  const [channelMenuPosition, setChannelMenuPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  // Compact rows hide tags behind an icon; tapping it opens a small
  // boundary-aware popover anchored to the touch point (see handleTagsPress).
  const [tagsPopoverContact, setTagsPopoverContact] = useState<Contact | null>(null);
  const [tagsPopoverPosition, setTagsPopoverPosition] = useState<{ x: number; y: number } | null>(null);

  // Use external search query if provided, otherwise use internal
  const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : internalSearchQuery;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [configuredServices, setConfiguredServices] = useState<{ whatsapp: boolean; facebook: boolean; xTwitter: boolean; phone: boolean }>({ whatsapp: false, facebook: false, xTwitter: false, phone: false });
  const [assignmentModalVisible, setAssignmentModalVisible] = useState(false);
  const [selectedContactForAssignment, setSelectedContactForAssignment] = useState<Contact | null>(null);
  const [bulkAssignVisible, setBulkAssignVisible] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkAssignMode, setBulkAssignMode] = useState<'assign' | 'unassign'>('assign');
  const [bulkTagVisible, setBulkTagVisible] = useState(false);
  const [bulkTagAction, setBulkTagAction] = useState<'add' | 'remove'>('add');
  const [bulkTagInput, setBulkTagInput] = useState('');
  // Remove mode picks from the business's EXISTING tags (search + tap to select) —
  // typing a tag name to remove it blindly was error-prone (a typo silently
  // removed nothing) and gave no sense of what tags a contact could even have.
  // Add mode keeps free text (bulkTagInput above), since a genuinely new tag
  // can't be "selected" from a list that doesn't have it yet.
  const [bulkRemoveTagsSelected, setBulkRemoveTagsSelected] = useState<string[]>([]);
  const [bulkTagSearch, setBulkTagSearch] = useState('');
  const [bulkTagging, setBulkTagging] = useState(false);
  // Add/remove selected contacts to/from a system Contacts Data List —
  // mirrors the bulk tag modal's add/remove toggle, but picks a list
  // instead of typing free text (only system:'contacts' lists are eligible,
  // same set as availableContactDataLists below/the filter dropdown, since
  // linking is only meaningful for a list this contact could actually
  // belong to as a real module record).
  const [bulkListVisible, setBulkListVisible] = useState(false);
  const [bulkListAction, setBulkListAction] = useState<'add' | 'remove'>('add');
  const [bulkListTargetId, setBulkListTargetId] = useState<string | null>(null);
  const [bulkListSaving, setBulkListSaving] = useState(false);
  // "+ New list" inside the dialog, and a plan without data lists (shown as locked, not hidden).
  const [bulkListNewName, setBulkListNewName] = useState('');
  const [bulkListCreating, setBulkListCreating] = useState(false);
  const [bulkListLocked, setBulkListLocked] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  // Multi-select tag filter: each available tag can be untouched, marked to
  // include (contact must have it), or marked to exclude (contact must NOT
  // have it) — replaces the old single-tag exact-match filter.
  const [includeTags, setIncludeTags] = useContactsFilterField('includeTags');
  const [excludeTags, setExcludeTags] = useContactsFilterField('excludeTags');
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [tagFilterSearch, setTagFilterSearch] = useState('');
  const [showTagMenu, setShowTagMenu] = useState(false);
  // The combined Filters menu renders every matching tag/data-list at once
  // with no cap — on a business with many tags this made the menu itself
  // require a lot of scrolling just to reach Location/Sort/Analytical below
  // it. FILTER_LIST_PAGE_SIZE + a "Load more" row keeps the initial render
  // light and the list short; each section also gets its OWN bounded,
  // independently-scrollable area (see tagListScroll/dataListScroll styles)
  // so scrolling through tags doesn't scroll the whole menu past them.
  const FILTER_LIST_PAGE_SIZE = 20;
  const [tagDisplayCount, setTagDisplayCount] = useState(FILTER_LIST_PAGE_SIZE);
  const [dataListDisplayCount, setDataListDisplayCount] = useState(FILTER_LIST_PAGE_SIZE);
  // Filter contacts down to those added through given System Datalists (see
  // DataListSystemMembershipModel on the backend) — a fourth filter
  // dimension alongside channel/assignment/tags, resolved server-side.
  // Same include/exclude cycle as tags (tap once to include, again to
  // exclude, again to clear) rather than the old single-select radio —
  // a list can be marked to include (contact must be a member of at least
  // one included list) or exclude (contact must be a member of none of the
  // excluded lists).
  const [includeDataListIds, setIncludeDataListIds] = useContactsFilterField('includeDataListIds');
  const [excludeDataListIds, setExcludeDataListIds] = useContactsFilterField('excludeDataListIds');
  // initialDataListFilter only seeds the state above on first mount — since
  // this component stays mounted across ContactsListScreen's "Exit to
  // Contacts"/"Exit to Data Lists" navigation (props change, no remount),
  // without this the filter (and the query it drives) kept the old scoped
  // Data List selected even after the caller's own scope had cleared.
  useEffect(() => {
    setIncludeDataListIds(initialDataListFilter ? [initialDataListFilter.id] : []);
    setExcludeDataListIds([]);
  }, [initialDataListFilter]);
  const [availableContactDataLists, setAvailableContactDataLists] = useState<Array<{ _id: string; name: string }>>([]);
  const [showDataListMenu, setShowDataListMenu] = useState(false);

  // Location filter (city/country — multi-select, exact match + radius
  // around a point) and sort (nearest/farthest from a point) — server-side
  // (OmniContactService.list's filterCities/filterCountries/nearLatitude/
  // nearLongitude/radiusKm/sortByDistance), backed by the 2dsphere index on
  // profile.location.coordinates. The reference point for nearest/farthest
  // is either the VIEWER's own current position (requested fresh via the
  // browser/device only when that sort is picked, rather than eagerly) OR
  // a point they tap on the map (mapPickedPoint) — either way it lands in
  // the same nearPoint used by the query.
  const [showLocationMenu, setShowLocationMenu] = useState(false);
  const [filterCities, setFilterCities] = useContactsFilterField('filterCities');
  const [filterCountries, setFilterCountries] = useContactsFilterField('filterCountries');
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [showCityMenu, setShowCityMenu] = useState(false);
  const [showCountryMenu, setShowCountryMenu] = useState(false);
  const [countryFilterSearch, setCountryFilterSearch] = useState('');
  const [cityFilterSearch, setCityFilterSearch] = useState('');
  const [radiusKm, setRadiusKm] = useContactsFilterField('radiusKm');
  // Explicit opt-in — fully independent of sortByDistance below. Previously
  // the radius input only appeared (and only ever applied) alongside a
  // chosen sort direction, and radiusKm silently became $geoNear's
  // maxDistance regardless of direction — so picking "Farthest" while a
  // radius value was still filled in from an earlier search wrongly
  // restricted results to WITHIN that radius first, then sorted that tiny
  // set descending, finding nobody genuinely far away. Radius now only
  // restricts results when this is explicitly on, in either sort
  // direction — or with no sort at all, as a pure "show only nearby
  // contacts" filter.
  const [radiusFilterEnabled, setRadiusFilterEnabled] = useContactsFilterField('radiusFilterEnabled');
  const [sortByDistance, setSortByDistance] = useContactsFilterField('sortByDistance');
  // Contacts with only city/address/country TEXT and no dropped pin are
  // otherwise invisible to a coordinate-only radius/nearest-farthest query
  // — on by default so they still show up (server backfills their
  // coordinates from that text in the background, and appends them
  // unordered in the meantime) rather than silently vanishing.
  const [includeUnlocated, setIncludeUnlocated] = useContactsFilterField('includeUnlocated');
  // One-shot delayed silent reload after a distance query — the server
  // returns those unlocated contacts immediately (fast, unordered/trailing)
  // while geocoding them in the background; this re-fetches a few seconds
  // later so the list quietly "catches up" to the correct interleaved
  // order once real coordinates land, without the user having to manually
  // re-trigger the filter. Tracked in a ref (not state) purely to clear a
  // stale timer if the filter changes again before it fires.
  const backfillCatchUpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contactUpdateReloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Confirmed real risk: search-text changes and filter changes (customFieldFilters,
  // analyticalFilters, sort, tags, ...) each fire their OWN separate loadContacts(true)
  // call from independent useEffects — e.g. toggling "Never Contacted" then typing a
  // search moments later fires two concurrent requests. With no sequencing, whichever
  // response happens to arrive LAST wins, even if it's the older, less-specific one
  // (filter-only, no search text) — silently overwriting the correct combined
  // filter+search result with a broader/stale one. Every non-loadMore loadContacts
  // call gets a new id here; a response is only applied if it's still the latest
  // request by the time it resolves, so a slow, superseded request can never clobber
  // a newer one's result regardless of network timing.
  const loadContactsRequestIdRef = useRef(0);
  const [nearPoint, setNearPoint] = useContactsFilterField('nearPoint');
  const [nearPointLabel, setNearPointLabel] = useContactsFilterField('nearPointLabel');
  const [locatingUser, setLocatingUser] = useState(false);
  const [showMapPointPicker, setShowMapPointPicker] = useState(false);
  const locationButtonRef = useRef<View | null>(null);
  const [locationMenuPosition, setLocationMenuPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const pageSizeButtonRef = useRef<View | null>(null);
  const [showPageSizeMenu, setShowPageSizeMenu] = useState(false);
  const [pageSizeMenuPosition, setPageSizeMenuPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  // Sort-by-field + custom-field filters — server-side (OmniContactService.
  // list's sortBy/sortOrder/customFieldFilters). sortBy is either one of
  // CONTACT_SORT_FIELDS' fixed ids or a literal profile.customFields key
  // (custom fields vary per business/import, so their keys come from
  // getContactCustomFieldKeys rather than a static list). Distance sort
  // (the location menu's nearest/farthest) is a separate, older mechanism —
  // picking a field sort here doesn't touch it, and vice versa; the backend
  // gives sortByDistance precedence when both are somehow set.
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortBy, setSortBy] = useContactsFilterField('sortBy');
  const [sortOrder, setSortOrder] = useContactsFilterField('sortOrder');
  const [customFieldKeys, setCustomFieldKeys] = useState<string[]>([]);
  const [customFieldFilters, setCustomFieldFilters] = useContactsFilterField('customFieldFilters');
  const [customFieldValueOptions, setCustomFieldValueOptions] = useState<Record<string, string[]>>({});
  const [loadingCustomFieldValues, setLoadingCustomFieldValues] = useState<string | null>(null);
  const [showAnalyticalFiltersModal, setShowAnalyticalFiltersModal] = useState(false);
  const [analyticalFilters, setAnalyticalFilters] = useContactsFilterField('analyticalFilters');
  const resetContactsFilters = useResetContactsFilters();
  const [pageSize, setPageSize] = useContactsFilterField('pageSize');
  // Draft text for the page-size input — a free-typed value, not a preset list
  // (per explicit correction: chips were replaced with this). Kept separate
  // from `pageSize` itself so a half-typed number (or a momentarily-empty
  // field while deleting) doesn't reload the list on every keystroke; the
  // real value only commits (floored at PAGE_SIZE_MIN, no upper cap) on blur/submit.
  const [pageSizeDraft, setPageSizeDraft] = useState(String(pageSize || 50));
  useEffect(() => { setPageSizeDraft(String(pageSize || 50)); }, [pageSize]);
  const commitPageSizeDraft = () => {
    const parsed = parseInt(pageSizeDraft, 10);
    const clamped = Number.isFinite(parsed) ? Math.max(PAGE_SIZE_MIN, parsed) : pageSize;
    setPageSizeDraft(String(clamped));
    if (clamped !== pageSize) setPageSize(clamped);
  };

  // An explicit initialFilter from navigation (HomeScreen's outreach-
  // guidance cards) applies the matching filter a user would otherwise have
  // to set manually — used by both the full Contacts screen and the web
  // right-pane, since both render this same component.
  useEffect(() => {
    if (routeParams?.initialFilter === 'neverContacted') {
      setAnalyticalFilters((prev) => ({ ...prev, neverContacted: true }));
    } else if (routeParams?.initialFilter === 'needsResponse') {
      setAnalyticalFilters((prev) => ({ ...prev, needsResponse: true }));
    } else if (routeParams?.initialFilter === 'followUpDue') {
      setAnalyticalFilters((prev) => ({ ...prev, noReplyDays: prev.noReplyDays || 3 }));
    } else if (routeParams?.initialFilter === 'expiringSoon') {
      setAnalyticalFilters((prev) => ({ ...prev, expiringSoon: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParams?.initialFilter]);

  // filterable: false for createdAt/lastActivityAt (sort-only — a checklist
  // of raw timestamps isn't a meaningful filter) and for city/country
  // (already have their own dedicated, working filter UI in the location
  // menu above — kept there rather than duplicated here to avoid two
  // competing filter surfaces for the same field).
  const CONTACT_SORT_FIELDS: Array<{ id: string; label: string; filterable: boolean; hasValueOnly?: boolean }> = [
    { id: 'name', label: t('contacts.sort.field.name'), filterable: true },
    { id: 'phone', label: t('contacts.sort.field.phone'), filterable: true },
    { id: 'email', label: t('contacts.sort.field.email'), filterable: true },
    { id: 'city', label: t('contacts.sort.field.city'), filterable: false },
    { id: 'country', label: t('contacts.sort.field.country'), filterable: false },
    { id: 'nationality', label: t('contacts.sort.field.nationality'), filterable: true },
    // Compound presence check across coordinates/address/city/country (the
    // same signal visit creation itself requires) — has no literal value
    // list of its own (there's nothing to pick "a value" of), so it's only
    // ever meaningful via the "Has a value"/"No value" chips, never the
    // values checklist below them. Lets a user find contacts missing a
    // location entirely (e.g. to fix before a visit campaign) or confirm
    // which ones already have one.
    { id: 'location', label: t('contacts.sort.field.location'), filterable: true, hasValueOnly: true },
    // Same reverse-relation-style presence check as location above — "has
    // this contact ever been linked to ANY channel (WhatsApp/Facebook/X)",
    // not a literal value to pick. hasValue:false is exactly "Never
    // Contacted" (see omni-contact.service.ts's CONTACT_SORT_FIELD_PATHS
    // and OmniChannelService.getOutreachSummary, which use this identical
    // filter server-side for the Home page guidance widget).
    { id: 'hasChannels', label: t('contacts.sort.field.hasChannels'), filterable: true, hasValueOnly: true },
    // needsResponse/followUpDue/expiringSoon moved out of this generic
    // checklist modal entirely — see ContactAnalyticalFiltersModal, opened
    // via its own toolbar icon. That modal gives followUpDue a real
    // user-typed day threshold (this checklist only ever offered a fixed
    // Has/No-value toggle) and keeps needsResponse/expiringSoon alongside
    // it and the new order-date-range filter, instead of splitting related
    // "analytical" filters across two different modals.
    { id: 'createdAt', label: t('contacts.sort.field.createdAt'), filterable: false },
    { id: 'lastActivityAt', label: t('contacts.sort.field.lastActivityAt'), filterable: false },
  ];

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreContacts, setHasMoreContacts] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [apiBaseURL, setApiBaseURL] = useState<string | null>(null);
  const menuMaxHeight = Math.max(240, Math.min(520, screenHeight - 160));

  useEffect(() => {
    if (onContactsUpdated) {
      onContactsUpdated(contacts);
    }
  }, [contacts, onContactsUpdated]);

  // Load API base URL for image proxy
  useEffect(() => {
    const loadApiBaseURL = async () => {
      const baseURL = await getApiBaseURL();
      setApiBaseURL(baseURL);
    };
    loadApiBaseURL();
  }, []);
  
  // Get user role from token on mount
  useEffect(() => {
    const getUserRole = () => {
      try {
        const token = apiService.getAuthToken();
        console.log('🔍 [ContactsList] Getting user role, token exists:', !!token);
        
        if (token) {
          const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
          console.log('🔍 [ContactsList] Token payload:', payload);
          console.log('🔍 [ContactsList] Checking role fields:', {
            userRole: payload.userRole,
            role: payload.role,
            business: payload.business
          });
          
          // Check multiple possible role field names
          const role = payload.userRole || payload.role || payload.business?.role;
          setUserRole(role);
          console.log('👤 [ContactsList] User role set to:', role);
        } else {
          console.warn('⚠️ [ContactsList] No token found');
        }
      } catch (error) {
        console.error('❌ [ContactsList] Error getting user role:', error);
      }
    };
    getUserRole();
  }, []);

  // Check if user is admin or owner
  const isAdminOrOwner = userRole === 'owner' || userRole === 'admin';
  const { hasPermissionAtLeast } = usePermissions();
  const canManageTags = isAdminOrOwner || hasPermissionAtLeast('contacts.manageTags', 'edit');
  // Assignment visibility/changes are governed entirely by contacts.manageAssignments now —
  // replacing the isAdminOrOwner role check per product decision (not OR'd like tags above).
  const canManageContactAssignments = hasPermissionAtLeast('contacts.manageAssignments', 'edit');
  // Same gate ContactCreateScreen/ContactsListScreen already use for
  // linkDataListRecords — general contact management, not a separate
  // permission for list membership specifically.
  const canManageDataListMembership = hasPermissionAtLeast('contacts.manage', 'edit');
  // Matches the backend's own gate on POST /api/visits (visits.manageVisits
  // at edit+) — "can this user create a visit at all", independent of
  // visits.execute (running an already-assigned visit) or visits.view.
  const canCreateVisits = hasPermissionAtLeast('visits.manageVisits', 'edit');

  // Whether ANY filter (not the free-text search) is currently applied —
  // drives the toolbar-level "Clear" shortcut so resetting doesn't require
  // opening the combined Filters menu first. Deliberately broader than the
  // Filters button's own active-highlight check above it (that one omits
  // location/radius), since a location filter left active with everything
  // else cleared should still surface a way to clear it from the toolbar.
  const hasActiveFilters = !!(
    selectedProvider || includeAssignmentFilters.length > 0 || excludeAssignmentFilters.length > 0 ||
    includeTags.length > 0 || excludeTags.length > 0 ||
    includeDataListIds.length > 0 || excludeDataListIds.length > 0 ||
    filterCities.length > 0 || filterCountries.length > 0 ||
    (radiusFilterEnabled && !!nearPoint && !!radiusKm.trim()) ||
    (sortByDistance && !!nearPoint) ||
    sortBy ||
    Object.values(customFieldFilters).some(v => v.length > 0) ||
    countActiveAnalyticalFilters(analyticalFilters) > 0
  );

  // Every active filter/sort param as one object — shared between
  // loadContacts' own paginated query below and onFiltersChanged (reports
  // this same shape upward so a parent screen's "export ALL" can re-run
  // the identical filter server-side with a much higher limit, instead of
  // exporting literally every contact in the business regardless of what's
  // currently filtered/searched on screen).
  const buildFilterParams = () => ({
    search: debouncedSearchQuery || undefined,
    includeTags: includeTags.length > 0 ? includeTags : undefined,
    excludeTags: excludeTags.length > 0 ? excludeTags : undefined,
    includeDataListIds: includeDataListIds.length > 0 ? includeDataListIds : undefined,
    excludeDataListIds: excludeDataListIds.length > 0 ? excludeDataListIds : undefined,
    filterCities: filterCities.length > 0 ? filterCities : undefined,
    filterCountries: filterCountries.length > 0 ? filterCountries : undefined,
    // Sent to the server (not filtered client-side) so pagination/hasMore and
    // "no results" reflect the real filtered set — a client-only filter over
    // just the currently-loaded page could show an empty page with "Load
    // more" stuck even though later pages had matches.
    includeAssignmentFilters: includeAssignmentFilters.length > 0 ? includeAssignmentFilters : undefined,
    excludeAssignmentFilters: excludeAssignmentFilters.length > 0 ? excludeAssignmentFilters : undefined,
    nearLatitude: nearPoint?.latitude,
    nearLongitude: nearPoint?.longitude,
    radiusKm: radiusFilterEnabled && radiusKm.trim() ? parseFloat(radiusKm.trim()) : undefined,
    radiusFilterEnabled: radiusFilterEnabled && !!nearPoint && !!radiusKm.trim() ? true : undefined,
    sortByDistance: sortByDistance && nearPoint ? sortByDistance : undefined,
    includeUnlocated: ((radiusFilterEnabled && radiusKm.trim()) || (sortByDistance && nearPoint)) ? includeUnlocated : undefined,
    sortBy: sortBy || undefined,
    sortOrder: sortBy ? sortOrder : undefined,
    // A field's selection is either a "Has a value"/"No value" sentinel
    // (translated here into the backend's hasValue flag) or a set of real
    // literal values — never both, enforced by the modal's own toggle
    // logic (see ContactSortFilterModal's toggleHasValueSentinel).
    customFieldFilters: [
      ...Object.entries(customFieldFilters)
        .filter(([, values]) => values.length > 0)
        .map(([key, values]) => {
          if (values.includes(HAS_VALUE_SENTINEL)) return { key, values: [], hasValue: true };
          if (values.includes(NO_VALUE_SENTINEL)) return { key, values: [], hasValue: false };
          return { key, values };
        }),
      // needsResponse/expiringSoon are plain booleans — still go through
      // the same key/hasValue shape the backend already understands.
      ...(analyticalFilters.needsResponse ? [{ key: 'needsResponse', values: [], hasValue: true }] : []),
      ...(analyticalFilters.expiringSoon ? [{ key: 'expiringSoon', values: [], hasValue: true }] : []),
      // neverContacted reuses the exact same "hasChannels: no value" signal
      // the Sort & Filter modal's own hasChannels field and the Home
      // widget's neverContacted navigation already send.
      ...(analyticalFilters.neverContacted ? [{ key: 'hasChannels', values: [], hasValue: false }] : []),
    ],
    // Parameterized "no reply in at least N days" — replaces the old fixed
    // 3-day followUpDue toggle with a real, user-typed threshold.
    noReplyDays: analyticalFilters.noReplyDays || undefined,
    // "Has/hasn't ordered within [date range]" — a genuinely new dimension
    // (Orders, not messaging), only sent once both a range AND a has/no
    // mode are picked.
    orderDateFrom: analyticalFilters.hasOrderInRange !== undefined ? analyticalFilters.orderDateFrom?.toISOString() : undefined,
    orderDateTo: analyticalFilters.hasOrderInRange !== undefined ? analyticalFilters.orderDateTo?.toISOString() : undefined,
    hasOrderInRange: analyticalFilters.hasOrderInRange,
    // "Contacted by any/specific channel within [date range]" — only sent
    // once both a range AND a was/wasn't-contacted mode are picked.
    contactedChannel: analyticalFilters.hasContactedInRange !== undefined ? analyticalFilters.contactedChannel : undefined,
    contactedDateFrom: analyticalFilters.hasContactedInRange !== undefined ? analyticalFilters.contactedDateFrom?.toISOString() : undefined,
    contactedDateTo: analyticalFilters.hasContactedInRange !== undefined ? analyticalFilters.contactedDateTo?.toISOString() : undefined,
    hasContactedInRange: analyticalFilters.hasContactedInRange,
    // "Contacted us back N+ times" — a real, user-typed threshold.
    minMessageCount: analyticalFilters.minMessageCount || undefined,
  });

  // Inbox mode: each loaded contact's latest conversation, for its row (preview, time, unread)
  // and for opening that exact conversation.
  const attachInboxConversations = async (ids: string[], requestId: number) => {
    if (!ids.length) return;
    try {
      const res = await apiService.getUnifiedConversations({ parentContactIds: ids, limit: ids.length, groupByContact: true });
      if (requestId !== loadContactsRequestIdRef.current || res.statusCode !== 1000) return;
      const byContact = new Map<string, any>();
      (res.data?.conversations || []).forEach((row: any) => {
        if (row.parentContactId) byContact.set(String(row.parentContactId), row);
      });
      setContacts(prev => prev.map(c => byContact.has(c.id) ? ({ ...c, inboxConversation: byContact.get(c.id) } as Contact) : c));
    } catch (error) {
      console.error('❌ [ContactsList] Failed to load inbox conversations:', error);
    }
  };

  // Load unified contacts from API with pagination
  const loadContacts = async (refresh = false, loadMore = false) => {
    // Claims this call as the latest in-flight request — see loadContactsRequestIdRef's
    // own doc comment for the race this closes. A loadMore call is an appended
    // continuation of whatever the latest reset request was, not a competing new
    // query, so it doesn't claim a new id itself; it just checks against whatever
    // the current one is when its response lands.
    const requestId = loadMore ? loadContactsRequestIdRef.current : ++loadContactsRequestIdRef.current;
    try {
      if (refresh) {
        setRefreshing(true);
        setCurrentPage(0);
        setHasMoreContacts(true);
      } else if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setCurrentPage(0);
        setHasMoreContacts(true);
      }

      const page = refresh ? 0 : (loadMore ? currentPage + 1 : currentPage);
      const limit = pageSize || 50; // Contacts per page — user-controlled, persisted (see pageSize)

      console.log('🔍 [ContactsList] Loading parent contacts...', { page, limit, searchQuery: debouncedSearchQuery });

      const response = await apiService.getParentContacts({
        limit,
        offset: page * limit,
        ...buildFilterParams(),
        ...(isInbox ? { inbox: true, inboxStatus: inboxStatus !== 'all' ? inboxStatus : undefined } : {}),
      });
      console.log('🔍 [ContactsList] Parent contacts response:', response);

      // A newer reset request (filter/search change) has since superseded this
      // one — applying this response now would overwrite correct, newer results
      // with a stale/broader set purely because of network timing. Silently
      // drop it; the newer request's own response (already in flight or about
      // to be) is what the screen should end up showing.
      if (requestId !== loadContactsRequestIdRef.current) {
        console.log('🔍 [ContactsList] Discarding stale loadContacts response', { requestId, current: loadContactsRequestIdRef.current });
        return;
      }

      if (response.data && response.statusCode === 1000) {
        // API returns { contacts, totalCount } or legacy array
        const responseData = response.data;
        const parentContacts = Array.isArray(responseData)
          ? responseData
          : (responseData.contacts || []);
        const apiTotalCount: number = Array.isArray(responseData)
          ? 0
          : (responseData.totalCount ?? 0);
        
        // Map parent contacts to Contact format
        const contactsData: Contact[] = parentContacts.map((parentContact: any) => {
          // Extract phone and email from identifiers (can be array or object)
          let phoneValue = parentContact.profile?.phone;
          let emailValue = parentContact.profile?.email;
          
          if (!phoneValue && parentContact.identifiers) {
            if (Array.isArray(parentContact.identifiers)) {
              const phoneId = parentContact.identifiers.find((id: any) => 
                id.type === 'phone' || id.type === 'whatsapp'
              );
              phoneValue = phoneId?.value;
            } else if (typeof parentContact.identifiers === 'object') {
              phoneValue = firstText(parentContact.identifiers.phone) || firstText(parentContact.identifiers.whatsapp);
            }
          }
          
          if (!emailValue && parentContact.identifiers) {
            if (Array.isArray(parentContact.identifiers)) {
              const emailId = parentContact.identifiers.find((id: any) => id.type === 'email');
              emailValue = emailId?.value;
            } else if (typeof parentContact.identifiers === 'object') {
              emailValue = firstText(parentContact.identifiers.email);
            }
          }
          
          // Determine primary channel from linked channels — previously an
          // allowlist that only recognized facebook-messenger/telegram/
          // email and silently defaulted EVERYTHING else (including a
          // contact whose only linked channel was Phone or X/Twitter) to
          // 'whatsapp', actively mislabeling those contacts into the
          // WhatsApp group instead of their own. Trust whatever channel is
          // actually there; 'whatsapp' is only a fallback when there's
          // truly no linked channel at all.
          const linkedChannels = parentContact.linkedChannelContacts || [];
          const primaryChannel: Contact['channel'] = linkedChannels.length > 0 && linkedChannels[0].channel
            ? linkedChannels[0].channel
            : 'whatsapp';
          
          // Get last activity from communication history
          const lastActivity = parentContact.communicationHistory?.[0]?.lastMessageAt;

          // Channels this contact has ACTUALLY exchanged messages on — a
          // channel can be linked (an identity exists) without a single
          // message ever having been sent on it, which made the old
          // linkedChannels-based Channel filter show contacts under a
          // channel they'd never really communicated through.
          const communicatedChannels = Array.from(new Set(
            (parentContact.communicationHistory || [])
              .filter((h: any) => (h.messageCount || 0) > 0)
              .map((h: any) => h.channel)
              .filter(Boolean)
          )) as string[];

          return {
            id: parentContact._id || parentContact.id,
            name: parentContact.profile?.name || '',
            channel: primaryChannel,
            phoneNumber: phoneValue,
            phone: phoneValue,
            email: emailValue,
            profilePictureUrl: parentContact.profile?.avatar,
            isOnline: false, // TODO: Determine from linked channels
            lastSeen: lastActivity ? new Date(lastActivity).toISOString() : undefined,
            messageCount: parentContact.communicationHistory?.reduce((sum: number, h: any) => sum + (h.messageCount || 0), 0) || 0,
            source: parentContact.metadata?.source || 'omni-contact',
            createdAt: parentContact.createdAt || new Date().toISOString(),
            updatedAt: parentContact.updatedAt || new Date().toISOString(),
            createdBy: parentContact.metadata?.createdBy,
            // Tags
            tags: parentContact.profile?.tags || [],
            // Assignment information
            assignment: parentContact.assignment || null,
            assignments: parentContact.assignments || [],
            // Store linked channels for filtering
            linkedChannels: linkedChannels.map((lc: any) => lc.channel),
            communicatedChannels,
            // Store full parent contact data
            parentContact: parentContact,
          } as Contact & { linkedChannels?: string[]; communicatedChannels?: string[]; parentContact?: any };
        });
        
        // Determine configured services from linked channels
        const hasWhatsApp = contactsData.some((c: any) => c.linkedChannels?.includes('whatsapp'));
        const hasFacebook = contactsData.some((c: any) => c.linkedChannels?.includes('facebook-messenger'));
        const hasXTwitter = contactsData.some((c: any) => c.linkedChannels?.includes('x-twitter'));
        const hasPhone = contactsData.some((c: any) => c.linkedChannels?.includes('phone'));
        const configuredServices = {
          whatsapp: hasWhatsApp,
          facebook: hasFacebook,
          xTwitter: hasXTwitter,
          phone: hasPhone,
        };
        
        const realTotal = apiTotalCount > 0 ? apiTotalCount : 0;
        const loadedSoFar = loadMore ? contacts.length + contactsData.length : contactsData.length;
        const hasMore = realTotal > 0 ? loadedSoFar < realTotal : contactsData.length === limit;

        if (loadMore) {
          setContacts(prev => {
            const combined = [...prev, ...contactsData];
            return combined.filter((c, i, self) => i === self.findIndex(x => x.id === c.id));
          });
        } else {
          setContacts(contactsData.filter((c, i, self) => i === self.findIndex(x => x.id === c.id)));
        }

        if (isInbox) attachInboxConversations(contactsData.map(c => c.id), requestId);

        setCurrentPage(page);
        setHasMoreContacts(hasMore);
        setTotalCount(realTotal || loadedSoFar + (hasMore ? limit : 0));
        setConfiguredServices(configuredServices);
        console.log('✅ [ContactsList] Loaded contacts:', contactsData.length);

        // A distance query with includeUnlocated may have just kicked off
        // a background geocode pass server-side for any text-only contacts
        // it couldn't rank yet (see OmniContactService.list) — one silent
        // re-fetch a few seconds later picks up whatever that pass
        // resolved, without the user needing to re-open the filter.
        if (backfillCatchUpTimer.current) clearTimeout(backfillCatchUpTimer.current);
        const hasActiveDistanceQuery = !!nearPoint && ((radiusFilterEnabled && !!radiusKm.trim()) || !!sortByDistance);
        if (hasActiveDistanceQuery && includeUnlocated && !loadMore) {
          backfillCatchUpTimer.current = setTimeout(() => {
            loadContacts(true);
          }, 4000);
        }
      } else {
        console.error('❌ [ContactsList] Failed to load contacts:', response.errors);
        if (!loadMore) {
          setContacts([]);
          setFilteredContacts([]);
        }
      }
    } catch (error) {
      console.error('❌ [ContactsList] Error loading contacts:', error);
      // Same staleness guard as the success path — a superseded request's
      // network error must not clear results a newer, still-in-flight (or
      // already-succeeded) request already/will correctly show.
      if (!loadMore && requestId === loadContactsRequestIdRef.current) {
        setContacts([]);
        setFilteredContacts([]);
      }
    } finally {
      // A stale (superseded) request's completion must not flip loading/refreshing
      // back to false — the early "discard stale response" return above still
      // runs this finally block. Left unconditional, whichever of several
      // concurrently in-flight requests (e.g. the mount-time duplicate
      // reset calls below) happened to resolve FIRST would prematurely
      // clear the loading state while contacts/configuredServices were
      // still at their pristine empty initial values — flashing the "no
      // channels configured" empty state before the real (current)
      // request's response actually arrives a moment later.
      if (loadMore || requestId === loadContactsRequestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
      setLoadingMore(false);
    }
  };

  // Load more contacts
  const loadMoreContacts = () => {
    if (!loadingMore && hasMoreContacts) {
      console.log('🔍 [ContactsList] Loading more contacts...');
      loadContacts(false, true);
    }
  };

  // Load contacts on mount
  useEffect(() => {
    loadContacts();
  }, []);

  // Available tags for the tag filter menu and bulk-tag modal — loaded on
  // demand when either opens (not on mount), through the shared module-level
  // cache (see fetchTagsCached's own doc comment) so reopening doesn't refetch.
  const fetchTags = (forceRefresh = false) => {
    fetchTagsCached(forceRefresh).then(data => {
      if (data) setAvailableTags(data);
    });
  };

  // Fetch contacts-type System Datalists for the "filter by list" dropdown
  const fetchContactDataLists = () => {
    fetchContactDataListsCached().then(data => {
      if (data) setAvailableContactDataLists(data.map(l => ({ _id: l._id, name: l.name })));
    });
  };

  useEffect(() => {
    fetchContactDataLists();
  }, []);

  // Distinct city values already in use — city is a picker (not free text)
  // in the location filter, same reasoning as tags/data lists: a typo'd
  // value would silently match nothing server-side.
  const fetchLocationCities = () => {
    fetchLocationCitiesCached().then(data => {
      if (data) setAvailableCities(data);
    });
  };

  useEffect(() => {
    fetchLocationCities();
  }, []);

  // Distinct custom-field keys already in use — populates the "extra
  // fields" section of the sort/filter menu. Custom fields come from
  // whatever a bulk upload/webhook import didn't map to a fixed column, so
  // there's no static list to offer here.
  const fetchCustomFieldKeys = () => {
    fetchCustomFieldKeysCached().then(data => {
      if (data) setCustomFieldKeys(data);
    });
  };

  useEffect(() => {
    fetchCustomFieldKeys();
  }, []);

  // fieldId is either one of CONTACT_SORT_FIELDS' filterable ids (name/
  // phone/email/nationality) or a literal custom field key — both share the
  // same "seen values" checklist UX, just backed by two different
  // endpoints (fixed fields have a known document path; custom fields
  // don't). isFixedField tells this which one to call.
  const fetchFieldValues = (fieldId: string, isFixedField: boolean) => {
    setLoadingCustomFieldValues(fieldId);
    const request = isFixedField
      ? apiService.getContactFixedFieldValues(fieldId)
      : apiService.getContactCustomFieldValues(fieldId);
    request.then(res => {
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        const values: string[] = res.data;
        setCustomFieldValueOptions(prev => ({ ...prev, [fieldId]: values }));
      }
    }).catch(() => {
      // swallow — the checklist just stays empty for this field
    }).finally(() => {
      setLoadingCustomFieldValues(null);
    });
  };

  // Debounce search: without this, every keystroke fired a full network request and
  // reset the list to a loading spinner mid-typing — the main cause of search
  // feeling laggy/unusable on a list that can have hundreds of contacts.
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle search query changes - reload data from API
  useEffect(() => {
    if (debouncedSearchQuery !== undefined) {
      loadContacts(true);
    }
  }, [debouncedSearchQuery]);

  // Reload when tag or data-list filter changes
  const customFieldFiltersKey = JSON.stringify(customFieldFilters);
  // analyticalFilters (ContactAnalyticalFiltersModal's noReplyDays/
  // needsResponse/expiringSoon/order-date-range) was previously left out of
  // this effect's dependency list entirely — applying it changed state but
  // never re-triggered a fetch, so it silently had zero effect on results
  // until some unrelated change (search, sort, ...) happened to reload
  // afterward. Fixed by keying on its serialized value, same as
  // customFieldFiltersKey above.
  const analyticalFiltersKey = JSON.stringify(analyticalFilters);
  // Now a server-side filter (see buildFilterParams) — must be in this
  // dependency list or changing it silently has zero effect until some
  // unrelated change happens to reload afterward (same bug class as
  // analyticalFiltersKey above).
  const assignmentFilterKey = JSON.stringify({ includeAssignmentFilters, excludeAssignmentFilters });
  useEffect(() => {
    loadContacts(true);
  }, [includeTags, excludeTags, includeDataListIds, excludeDataListIds, filterCities, filterCountries, radiusKm, radiusFilterEnabled, sortByDistance, nearPoint, includeUnlocated, sortBy, sortOrder, customFieldFiltersKey, analyticalFiltersKey, assignmentFilterKey, pageSize, inboxStatus]);

  useEffect(() => {
    return () => {
      if (backfillCatchUpTimer.current) clearTimeout(backfillCatchUpTimer.current);
    };
  }, []);

  // Reports the SAME filter shape loadContacts just queried with, up to the
  // parent screen — so "export all" (which has no ids to send, unlike
  // selection-mode export) can ask the export endpoint to apply this same
  // filter server-side instead of exporting literally every contact in the
  // business regardless of what's currently searched/filtered on screen.
  useEffect(() => {
    onFiltersChanged?.(buildFilterParams());
  }, [debouncedSearchQuery, includeTags, excludeTags, includeDataListIds, excludeDataListIds, filterCities, filterCountries, radiusKm, radiusFilterEnabled, sortByDistance, nearPoint, includeUnlocated, sortBy, sortOrder, customFieldFiltersKey, analyticalFiltersKey, assignmentFilterKey]);

  // Handle refresh trigger - reload when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      console.log('🔄 [ContactsList] Refresh trigger changed, reloading contacts:', refreshTrigger);
      loadContacts(true); // Reload contacts
    }
  }, [refreshTrigger]);

  // Listen directly to cross-pane contact updates too. In the desktop layout
  // the visible list is owned by PersistentPaneShell's left pane, while list
  // membership changes can happen from the middle profile card.
  useEffect(() => {
    if (lastContactsRefreshTokenRef.current === contactsRefreshToken) return;
    lastContactsRefreshTokenRef.current = contactsRefreshToken;
    console.log('🔄 [ContactsList] Global contacts refresh token changed, reloading contacts:', contactsRefreshToken);
    loadContacts(true);
  }, [contactsRefreshToken]);

  // Filter contacts by provider (client-side filtering for provider only)
  useEffect(() => {
    let filtered = contacts;

    // Filter by CHANNEL COMMUNICATED (has actually exchanged messages on
    // that channel — communicatedChannels, derived from communicationHistory's
    // messageCount), not merely linked (an identity existing with zero
    // messages ever sent). A contact's primaryChannel can itself be a
    // linked-but-never-messaged channel, so that alone isn't checked here
    // either — communicatedChannels is the single source of truth for this
    // filter.
    if (selectedProvider) {
      filtered = filtered.filter(contact => {
        const contactWithChannels = contact as Contact & { communicatedChannels?: string[] };
        return contactWithChannels.communicatedChannels?.includes(selectedProvider);
      });
    }

    setFilteredContacts(filtered);
  }, [contacts, selectedProvider]);

  // Listen for real-time contact updates via Socket.IO
  useEffect(() => {
    const handleNewContact = (data: any) => {
      console.log('🔍 [ContactsList] New contact received via socket:', data);
      
      const newContact: Contact = {
        id: data.contactId,
        name: data.name || data.phoneNumber,
        channel: 'whatsapp',
        phoneNumber: data.phoneNumber,
        lastSeen: data.lastSeen,
        messageCount: 0,
        createdAt: data.timestamp,
        updatedAt: data.timestamp,
      };

      setContacts(prevContacts => {
        const exists = prevContacts.some(contact => contact.id === data.contactId);
        if (exists) return prevContacts;
        return [newContact, ...prevContacts];
      });
    };

    const handleContactUpdate = (data: any) => {
      console.log('🔍 [ContactsList] Contact update received via socket:', data);

      // A local field patch alone can never correctly decide whether this
      // contact should now appear/disappear from an active filter (e.g.
      // "Never Contacted" or "No reply in N days") — that membership is
      // computed server-side at fetch time. Debounced re-fetch instead, so
      // a burst of rapid interactions (several quick messages/calls)
      // collapses into one reload rather than one per event. This is what
      // makes a filtered contact disappear/appear the moment they're
      // actually contacted, instead of only on the next manual refresh.
      if (contactUpdateReloadTimer.current) clearTimeout(contactUpdateReloadTimer.current);
      contactUpdateReloadTimer.current = setTimeout(() => {
        loadContacts(true);
      }, 600);
    };

    const handleAppNotification = (data: any) => {
      try {
        const payload = typeof data?.data === 'string'
          ? JSON.parse(data.data)
          : (data?.data || data);

        const notificationType = payload?.type || data?.type;
        const action = payload?.action || data?.action;
        if (
          payload?.reloadContacts ||
          payload?.reloadAssignments ||
          action === 'removed' ||
          notificationType === 'new_assignment' ||
          notificationType === 'assignment_update'
        ) {
          console.log('🔄 [ContactsList] Reloading contacts due to assignment notification');
          loadContacts(true);
        }
      } catch (error) {
        console.warn('⚠️ [ContactsList] Failed to parse app notification for reload:', error);
      }
    };

    socketService.onNewContact(handleNewContact);
    socketService.onContactUpdate(handleContactUpdate);
    socketService.on('app-notification', handleAppNotification);

    return () => {
      socketService.offNewContact(handleNewContact);
      socketService.offContactUpdate(handleContactUpdate);
      socketService.off('app-notification', handleAppNotification);
      if (contactUpdateReloadTimer.current) clearTimeout(contactUpdateReloadTimer.current);
    };
  }, []);

  // Refresh handler
  const handleRefresh = () => {
    loadContacts(true);
  };

  // Handle contact press (row click - for editing). Wrapped in useCallback (and the
  // two handlers below) since they're passed as props into the memoized ContactRow —
  // without a stable reference, React.memo would be defeated on every parent render.
  const handleContactPress = useCallback((contact: Contact) => {
    if (selectionMode && onToggleSelect) {
      onToggleSelect(contact);
      return;
    }
    if (onContactPress) {
      onContactPress(contact);
    }
    // If no onContactPress handler, do nothing (don't start conversation)
  }, [selectionMode, onToggleSelect, onContactPress]);

  // Handle assignment badge press
  const handleAssignmentPress = useCallback((contact: Contact, event?: any) => {
    if (event) {
      event.stopPropagation(); // Prevent row click from firing
    }

    setSelectedContactForAssignment(contact);
    setAssignmentModalVisible(true);
  }, []);

  // Handle tags icon press (compact rows) — positions a boundary-aware
  // popover from the touch point itself (pageX/pageY), since a FlatList row
  // isn't a stable ref target the way the header's filter buttons are.
  const handleTagsPress = useCallback((contact: Contact, event?: any) => {
    if (event) {
      event.stopPropagation();
      const { pageX, pageY } = event.nativeEvent ?? {};
      if (typeof pageX === 'number' && typeof pageY === 'number') {
        setTagsPopoverPosition(toWindowRelative(pageX, pageY));
      }
    }
    setTagsPopoverContact(contact);
  }, []);

  const assignmentFilterMatches = useCallback((filter: AssignmentFilterTarget, target: AssignmentFilterTarget) => (
    filter.type === target.type && (filter.id || '') === (target.id || '')
  ), []);

  const getAssignmentFilterState = useCallback((target: AssignmentFilterTarget): 'include' | 'exclude' | null => {
    if (includeAssignmentFilters.some((filter) => assignmentFilterMatches(filter, target))) return 'include';
    if (excludeAssignmentFilters.some((filter) => assignmentFilterMatches(filter, target))) return 'exclude';
    return null;
  }, [assignmentFilterMatches, excludeAssignmentFilters, includeAssignmentFilters]);

  const cycleAssignmentFilter = useCallback((target: AssignmentFilterTarget) => {
    const currentState = getAssignmentFilterState(target);
    if (currentState === null) {
      setIncludeAssignmentFilters((current) => [...current.filter((filter) => !assignmentFilterMatches(filter, target)), target]);
      setExcludeAssignmentFilters((current) => current.filter((filter) => !assignmentFilterMatches(filter, target)));
      return;
    }
    if (currentState === 'include') {
      setIncludeAssignmentFilters((current) => current.filter((filter) => !assignmentFilterMatches(filter, target)));
      setExcludeAssignmentFilters((current) => [...current.filter((filter) => !assignmentFilterMatches(filter, target)), target]);
      return;
    }
    setExcludeAssignmentFilters((current) => current.filter((filter) => !assignmentFilterMatches(filter, target)));
  }, [assignmentFilterMatches, getAssignmentFilterState, setExcludeAssignmentFilters, setIncludeAssignmentFilters]);

  // Handle assignment change
  const handleAssignmentChange = async (assignmentData: { type: 'user' | 'department'; id: string }) => {
    // Reload contacts to reflect the new assignment - use refresh to get latest data from server
    console.log('📝 [ContactsList] Assignment changed, reloading contacts:', assignmentData);
    // Add a small delay to ensure backend notifications have been sent before reload
    await new Promise(resolve => setTimeout(resolve, 300));
    await loadContacts(true);
  };

  const getSelectedContacts = () => {
    return contacts.filter(contact => selectedSet.has(contact.id));
  };

  const handleOpenBulkAssign = () => {
    if (!canManageContactAssignments) return;
    if (selectedSet.size === 0) return;
    setBulkAssignMode('assign');
    setBulkAssignVisible(true);
    fetchAssignmentUsers();
    fetchAssignmentDepartments();
  };

  const handleBulkAssign = async (type: 'user' | 'department', id: string) => {
    if (!canManageContactAssignments || bulkAssigning) return;

    const selectedContacts = getSelectedContacts();
    if (selectedContacts.length === 0) {
      setBulkAssignVisible(false);
      return;
    }

    const user = assignmentUsers.find(u => u._id === id);
    const department = assignmentDepartments.find(d => d._id === id);
    const titleLabel = type === 'user'
      ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || 'User'
      : department?.name || 'Department';

    setBulkAssigning(true);
    try {
      const assignments = selectedContacts.map(contact => ({
        targetType: 'omni_contact',
        targetId: contact.id,
        assignedTo: type === 'user' ? id : undefined,
        departmentId: type === 'department' ? id : undefined,
        assignmentType: 'contact',
        title: `Contact Assignment - ${titleLabel}`,
        description: `Bulk assigned via mobile app`,
        status: 'active',
        priority: 'normal'
      }));

      await apiService.authenticatedRequest('/api/assignments/bulk', {
        method: 'POST',
        body: JSON.stringify({ assignments })
      });

      setBulkAssignVisible(false);
      onExitSelectionMode?.();
      await loadContacts(true);
    } catch (error: any) {
      console.error('❌ [ContactsList] Bulk assignment failed:', error);
      alert(t('contacts.errors.prefix', { message: error.message || t('contacts.errors.bulkAssignFailed') }));
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleBulkUnassign = async (type: 'user' | 'department', id: string) => {
    if (!canManageContactAssignments || bulkAssigning) return;

    const selectedContacts = getSelectedContacts();
    if (selectedContacts.length === 0) {
      setBulkAssignVisible(false);
      return;
    }

    setBulkAssigning(true);
    try {
      await apiService.authenticatedRequest('/api/assignments/bulk-unassign', {
        method: 'POST',
        body: JSON.stringify({
          targetIds: selectedContacts.map(contact => contact.id),
          assignedTo: type === 'user' ? id : undefined,
          departmentId: type === 'department' ? id : undefined,
        }),
      });

      setBulkAssignVisible(false);
      onExitSelectionMode?.();
      await loadContacts(true);
    } catch (error: any) {
      console.error('❌ [ContactsList] Bulk unassignment failed:', error);
      alert(t('contacts.errors.prefix', { message: error.message || t('contacts.errors.bulkUnassignFailed') }));
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleOpenBulkTag = () => {
    if (!canManageTags) return;
    if (selectedSet.size === 0) return;
    setBulkTagInput('');
    setBulkRemoveTagsSelected([]);
    setBulkTagSearch('');
    setBulkTagAction('add');
    fetchTags();
    setBulkTagVisible(true);
  };

  const toggleBulkRemoveTag = (tag: string) => {
    setBulkRemoveTagsSelected(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]));
  };

  // Add mode: comma/newline-separated free text, so a user can add several
  // (possibly brand-new) tags in one action. Remove mode: tags picked from
  // the searchable list below (see bulkRemoveTagsSelected).
  const handleBulkTagSubmit = async () => {
    if (bulkTagging) return;
    const tags = bulkTagAction === 'remove'
      ? bulkRemoveTagsSelected
      : bulkTagInput.split(/[,\n]/).map(t => t.trim()).filter(Boolean);
    if (tags.length === 0) return;

    const selectedContacts = getSelectedContacts();
    if (selectedContacts.length === 0) {
      setBulkTagVisible(false);
      return;
    }

    setBulkTagging(true);
    try {
      const response = await apiService.bulkUpdateContactTags(
        selectedContacts.map(c => c.id),
        tags,
        bulkTagAction
      );
      if (response.statusCode !== 1000) {
        throw new Error((response.errors as any[])?.[0]?.message || response.errors?.[0] as any || 'Failed to update tags');
      }

      setBulkTagVisible(false);
      onExitSelectionMode?.();
      await loadContacts(true);
      await fetchTags(true); // Force a fresh fetch (bypass the cache) in case a brand-new tag was added
    } catch (error: any) {
      console.error('❌ [ContactsList] Bulk tag update failed:', error);
      alert(t('contacts.errors.prefix', { message: error.message || t('contacts.errors.bulkTagFailed') }));
    } finally {
      setBulkTagging(false);
    }
  };

  const handleOpenBulkList = () => {
    if (!canManageDataListMembership) return;
    if (selectedSet.size === 0) return;
    setBulkListTargetId(null);
    setBulkListAction('add');
    setBulkListNewName('');
    setBulkListVisible(true);
    // Re-read the lists (one may have been made since) and learn whether the plan has them at all.
    apiService.getDataLists({ systemModule: 'contacts', limit: 100 }).then((res) => {
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setBulkListLocked(false);
        setAvailableContactDataLists(res.data.map((l) => ({ _id: l._id, name: l.name })));
      } else {
        setBulkListLocked((res.errors?.[0] as any)?.code === 'FEATURE_NOT_IN_PLAN');
      }
    }).catch(() => undefined);
  };

  /** "+ New list": a contacts list made here, selected right away. */
  const handleCreateBulkList = async () => {
    const name = bulkListNewName.trim();
    if (!name || bulkListCreating) return;
    setBulkListCreating(true);
    try {
      const res = await apiService.createDataList({ name, columnsSource: 'system', systemModule: 'contacts' } as any);
      if (res.statusCode === 1000 && res.data) {
        const list = { _id: (res.data as any)._id, name: (res.data as any).name };
        setAvailableContactDataLists((prev) => [list, ...prev]);
        setBulkListTargetId(list._id);
        setBulkListNewName('');
        fetchContactDataListsCached(true);
      } else {
        alert(res.errors?.[0]?.message || t('contacts.bulkList.createFailed'));
      }
    } catch (error: any) {
      alert(error?.message || t('contacts.bulkList.createFailed'));
    } finally {
      setBulkListCreating(false);
    }
  };

  const handleBulkListSubmit = async () => {
    if (bulkListSaving || !bulkListTargetId) return;

    const selectedContacts = getSelectedContacts();
    if (selectedContacts.length === 0) {
      setBulkListVisible(false);
      return;
    }

    setBulkListSaving(true);
    try {
      const recordIds = selectedContacts.map(c => c.id);
      const response = bulkListAction === 'add'
        ? await apiService.linkDataListRecords(bulkListTargetId, recordIds)
        : await apiService.unlinkDataListRecords(bulkListTargetId, recordIds);
      if (response.statusCode !== 1000) {
        throw new Error((response.errors as any[])?.[0]?.message || response.errors?.[0] as any || 'Failed to update list membership');
      }

      setBulkListVisible(false);
      onExitSelectionMode?.();
      await loadContacts(true);
    } catch (error: any) {
      console.error('❌ [ContactsList] Bulk data list membership update failed:', error);
      alert(t('contacts.errors.prefix', { message: error.message || t('contacts.errors.bulkListFailed') }));
    } finally {
      setBulkListSaving(false);
    }
  };

  // Contact rows in this list only ever carry an id/name (see the Contact
  // interface above — no address/lat/lng field is populated by loadContacts'
  // mapping), so prefill only forwards those two; VisitCreateScreen and
  // VisitCampaignCreateScreen's own contact search/pool step already resolve
  // full location data server-side once a contact is actually attached.
  const handleBulkCreateVisits = () => {
    if (!canCreateVisits) return;
    const selected = getSelectedContacts();
    if (selected.length === 0) return;

    if (selected.length === 1) {
      const c = selected[0];
      navigateTo('visit-create', undefined, {
        prefillContact: { contactId: c.id, name: c.name || '' },
      });
    } else {
      navigateTo('visit-campaign-create', undefined, {
        prefillContacts: selected.map(c => ({ contactId: c.id, name: c.name || '' })),
      });
    }
    onExitSelectionMode?.();
  };

  /** Requests the VIEWER's current position on demand (not stored, not
   * requested until this sort is actually picked) and uses it as the
   * distance reference point — same web/native split MapLocationPicker.tsx
   * already uses for its "use my current location" button. */
  const handleUseMyLocation = async (direction: 'nearest' | 'farthest') => {
    setLocatingUser(true);
    try {
      if (Platform.OS === 'web') {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
          alert(t('contacts.filter.location.geolocationUnavailable'));
          return;
        }
        await new Promise<void>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setNearPoint({ latitude: position.coords.latitude, longitude: position.coords.longitude });
              setNearPointLabel(null);
              setSortByDistance(direction);
              setRadiusKm(prev => prev || '5');
              resolve();
            },
            () => {
              alert(t('contacts.filter.location.geolocationFailed'));
              resolve();
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
          );
        });
      } else {
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== 'granted') {
          alert(t('contacts.filter.location.geolocationPermissionDenied'));
          return;
        }
        const position = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
        setNearPoint({ latitude: position.coords.latitude, longitude: position.coords.longitude });
        setNearPointLabel(null);
        setSortByDistance(direction);
        setRadiusKm(prev => prev || '5');
      }
    } finally {
      setLocatingUser(false);
    }
  };

  const fetchAssignmentUsers = async () => {
    if (assignmentUsersLoading) return;
    setAssignmentUsersLoading(true);
    try {
      const businessId = apiService.getBusinessIdFromToken();
      if (!businessId) return;
      const response = await apiService.authenticatedRequest(`/api/business/${businessId}/users`, {
        method: 'GET'
      });
      const responseData = (response as any).data ?? (response as any).Data;
      if (Array.isArray(responseData)) {
        const allUsers = responseData.map((bu: any) => ({
          _id: bu.user?._id || bu.userId,
          firstName: bu.user?.firstName || '',
          lastName: bu.user?.lastName || '',
          email: bu.user?.email || '',
          role: bu.role || ''
        }));
        const filteredUsers = allUsers.filter(u => !['admin', 'owner'].includes((u.role || '').toLowerCase()));
        setAssignmentUsers(filteredUsers);
      }
    } catch (error) {
      console.error('❌ [ContactsList] Error fetching assignment users:', error);
    } finally {
      setAssignmentUsersLoading(false);
    }
  };

  const fetchAssignmentDepartments = async () => {
    if (assignmentDepartmentsLoading) return;
    setAssignmentDepartmentsLoading(true);
    try {
      const businessId = apiService.getBusinessIdFromToken();
      if (!businessId) return;
      const response = await apiService.authenticatedRequest(`/api/departments/business/${businessId}`, {
        method: 'GET'
      });
      const responseData = (response as any).data ?? (response as any).Data;
      if (Array.isArray(responseData)) {
        setAssignmentDepartments(responseData.filter((d: any) => d.isActive !== false));
      }
    } catch (error) {
      console.error('❌ [ContactsList] Error fetching assignment departments:', error);
    } finally {
      setAssignmentDepartmentsLoading(false);
    }
  };

  // useCallback with empty deps: these are pure functions of their arguments only,
  // wrapped so their reference stays stable across renders — required for
  // React.memo on ContactRow (which receives them as props) to actually prevent
  // re-renders, not just look like it does.
  const getAssignmentLabels = useCallback((contact: Contact) => {
    const sourceAssignments = contact.assignments && contact.assignments.length > 0
      ? contact.assignments
      : contact.assignment
      ? [contact.assignment]
      : [];

    const activeAssignments = sourceAssignments.filter(isVisibleAssignment);

    const labels = activeAssignments
      .map(a => {
        if (a.assignedTo) {
          const firstName = a.assignedTo.firstName || '';
          const lastName = a.assignedTo.lastName || '';
          return `${firstName} ${lastName}`.trim();
        }
        if (a.departmentId?.name) {
          return a.departmentId.name;
        }
        return '';
      })
      .filter(Boolean);

    return Array.from(new Set(labels));
  }, []);

  const formatAssignmentLabel = useCallback((labels: string[]) => {
    if (labels.length === 0) return 'Unassigned';
    if (labels.length <= 2) return labels.join(', ');
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
  }, []);

  // Available channels to filter by — based on which channels contacts
  // have ACTUALLY communicated on (communicatedChannels), not merely
  // linked identities. The Channel filter is purely a filter criterion
  // now, not a grouping/section mechanism — the list itself always shows
  // one row per parent contact (see flatListItems below), matching how
  // Inbox rolls a contact up once across all its channels instead of
  // repeating it under every channel it happens to touch.
  // "No configured channels" only when none is really connected — from the
  // live channel lists, not from which channels existing contacts used (a
  // new business with a connected page has no contacts yet).
  const { whatsappPhones: liveWaPhones, facebookPages: liveFbPages, xAccounts: liveXAccounts, phoneAccount: livePhoneAccount, profileCompletion: liveCompletion } = useProfileCompletion();
  const hasConnectedChannel = liveWaPhones.length > 0 || liveFbPages.length > 0 || liveXAccounts.length > 0 || !!livePhoneAccount
    || (liveCompletion?.services || []).some((s: any) => ['wabu', 'fbm', 'x_twitter', 'phone'].includes(String(s.code).toLowerCase()) && s.status === 'completed');

  const providers = React.useMemo(() => {
    const availableProviders = new Set<string>();

    contacts.forEach(contact => {
      const contactWithChannels = contact as Contact & { communicatedChannels?: string[] };
      // No allowlist here — a channel this app doesn't otherwise recognize
      // still gets a filter option (falls back to a generic 'chatbubble'
      // icon via getProviderIcon) rather than silently having no way to
      // filter by it.
      contactWithChannels.communicatedChannels?.forEach(channel => {
        if (channel) availableProviders.add(channel);
      });
    });

    // Always include configured services even if nobody has communicated
    // on them yet — filtering by one then would just correctly return zero
    // contacts, not hide the option entirely.
    if (configuredServices.whatsapp) availableProviders.add('whatsapp');
    if (configuredServices.facebook) availableProviders.add('facebook-messenger');
    if (configuredServices.xTwitter) availableProviders.add('x-twitter');
    if (configuredServices.phone) availableProviders.add('phone');

    return Array.from(availableProviders);
  }, [contacts, configuredServices]);

  // Flattened, virtualized contact list for FlatList. Previously grouped
  // into per-channel sections with a header divider (WhatsApp/Facebook/...),
  // which repeated the SAME parent contact once per channel it was linked
  // to — a contact linked to both WhatsApp and Facebook showed up twice.
  // Explicitly corrected: this should be "grouped by the parent contact
  // like in Inbox" — one row per contact, no channel-based duplication.
  // Rows don't show per-channel icons either; a contact's channels are
  // visible in its profile.
  type ListItem = { type: 'contact'; key: string; contact: Contact };

  const flatListItems = React.useMemo<ListItem[]>(
    () => filteredContacts.map(contact => ({ type: 'contact' as const, key: contact.id, contact })),
    [filteredContacts]
  );
  const displayedTotalCount = selectedProvider ? filteredContacts.length : totalCount;

  return (
    <View style={[styles.container, containerStyle]}>
      {/* Global, cross-screen bulk-upload progress — polls for ANY still-
          running import job on this business, not just one started from
          this exact screen instance, so it survives navigating away from
          BulkContactUploadScreen entirely. Renders nothing when no job is
          currently processing. */}
      <BulkUploadProgressIndicator />

      {/* Header — when showTitleBar is false (the parent screen already
          renders its own title/back row, e.g. ContactsListScreen's
          FormHeader), this drops the header-bar styling (background/border/
          padding) entirely rather than just hiding the title text, so the
          remaining filter/selection buttons read as an ordinary inline
          control row instead of a second header-shaped bar stacked under
          the real one. */}
      {showHeader && (
        <View style={[styles.header, compact && { paddingVertical: spacing.sm }, !showTitleBar && styles.headerBare]}>
          {/* Title (or, when the parent hides its own title bar, that
              parent's search box) always gets its own row — stacking it
              above the filter chips rather than sharing a row with them
              avoids a real flexbox bug tried here previously: a flex:1
              sibling next to a flexWrap row doesn't reserve space for the
              wrapped row's 2nd/3rd lines, so they rendered UNDER the first
              sibling instead of pushing the whole block taller. Stacking
              sidesteps that entirely — each row sizes independently. */}
          {showTitleBar ? (
            <View style={[styles.headerLeft, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name={isInbox ? 'chatbubbles' : 'people'} size={compact ? 16 : 20} color={colors.primary} />
              {!compact && <Text style={styles.headerTitle}>{t(isInbox ? 'inbox.title' : 'contacts.header.title')}</Text>}
              <Text style={styles.contactCount}>
                ({selectionMode && selectedSet.size > 0 ? t('contacts.header.selectedCount', { count: selectedSet.size }) : displayedTotalCount})
              </Text>
            </View>
          ) : headerLeftContent ? (
            <View style={[styles.headerLeft, { flexDirection: rtl.rowDirection }]}>
              {headerLeftContent}
            </View>
          ) : null}

          <View
            style={[
              styles.headerRight,
              { flexDirection: rtl.rowDirection, flexWrap: 'wrap' },
            ]}
          >
            {selectionMode ? (
              <>
                <TouchableOpacity
                  style={[styles.channelFilterButton, compact && styles.channelFilterButtonCompact]}
                  onPress={() => onSelectAllFiltered?.(filteredContacts)}
                >
                  <Ionicons name="checkbox-outline" size={compact ? 16 : 20} color={colors.primary} />
                </TouchableOpacity>
                {canManageContactAssignments && (
                  <TouchableOpacity
                    style={[styles.channelFilterButton, compact && styles.channelFilterButtonCompact]}
                    onPress={handleOpenBulkAssign}
                    disabled={selectedSet.size === 0}
                  >
                    <Ionicons
                      name="person-add-outline"
                      size={compact ? 16 : 20}
                      color={selectedSet.size === 0 ? colors.text.secondary : colors.primary}
                    />
                  </TouchableOpacity>
                )}
                {canManageTags && (
                  <TouchableOpacity
                    style={[styles.channelFilterButton, compact && styles.channelFilterButtonCompact]}
                    onPress={handleOpenBulkTag}
                    disabled={selectedSet.size === 0}
                  >
                    <Ionicons
                      name="pricetags-outline"
                      size={compact ? 16 : 20}
                      color={selectedSet.size === 0 ? colors.text.secondary : colors.primary}
                    />
                  </TouchableOpacity>
                )}
                {canManageDataListMembership && (
                  <TouchableOpacity
                    style={[styles.channelFilterButton, compact && styles.channelFilterButtonCompact]}
                    onPress={handleOpenBulkList}
                    disabled={selectedSet.size === 0}
                    accessibilityLabel={t('contacts.bulkList.title')}
                    accessibilityRole="button"
                  >
                    <Ionicons
                      name="list-outline"
                      size={compact ? 16 : 20}
                      color={selectedSet.size === 0 ? colors.text.secondary : colors.primary}
                    />
                  </TouchableOpacity>
                )}
                {canCreateVisits && (
                  <TouchableOpacity
                    style={[styles.channelFilterButton, compact && styles.channelFilterButtonCompact]}
                    onPress={handleBulkCreateVisits}
                    disabled={selectedSet.size === 0}
                    accessibilityLabel={t('contacts.header.createVisits')}
                    accessibilityRole="button"
                  >
                    <Ionicons
                      name="walk-outline"
                      size={compact ? 16 : 20}
                      color={selectedSet.size === 0 ? colors.text.secondary : colors.primary}
                    />
                  </TouchableOpacity>
                )}
                <TouchableOpacity
                  style={[styles.channelFilterButton, compact && styles.channelFilterButtonCompact]}
                  onPress={onExitSelectionMode}
                >
                  <Ionicons name="close" size={compact ? 16 : 20} color={colors.primary} />
                </TouchableOpacity>
              </>
            ) : (
              <>
                {onEnterSelectionMode && (
                  <TouchableOpacity
                    style={[styles.channelFilterButton, compact && styles.channelFilterButtonCompact]}
                    onPress={onEnterSelectionMode}
                    accessibilityLabel={t('contacts.header.selectContacts')}
                    accessibilityRole="button"
                  >
                    <Ionicons name="checkbox-outline" size={compact ? 16 : 20} color={colors.primary} />
                  </TouchableOpacity>
                )}
                {showFilters && hasActiveFilters && (
                  <TouchableOpacity
                    // @ts-ignore — title is a real DOM hover-tooltip attribute RNW forwards, not in the cross-platform type
                    title={t('contacts.filter.clearAll')}
                    style={[styles.channelFilterButton, compact && styles.channelFilterButtonCompact, { borderColor: colors.error }]}
                    onPress={() => { resetContactsFilters(); }}
                    accessibilityLabel={t('contacts.filter.clearAll')}
                    accessibilityRole="button"
                  >
                    <Ionicons name="close-circle-outline" size={compact ? 14 : 18} color={colors.error} />
                  </TouchableOpacity>
                )}
                {showFilters && (
                  /* One bordered "group" pill instead of 8 separate bordered
                     boxes — each icon still opens its own dedicated modal
                     (see the standalone Channel/Assignment/Tag/etc. modals
                     below), but visually they now read as one toolbar
                     control instead of 8 competing ones, and dropping each
                     icon's own label (kept only as a web hover tooltip +
                     accessibilityLabel, since there's no hover on touch)
                     cuts the group's footprint drastically — this is what
                     was still "taking a lot of space" with labels on. */
                  <View style={[styles.filterChipGroup, { flexDirection: rtl.rowDirection }]}>
                    <TouchableOpacity
                      ref={channelButtonRef}
                      // @ts-ignore — title is a real DOM hover-tooltip attribute RNW forwards, not in the cross-platform type
                      title={selectedProvider ? getProviderName(selectedProvider, t) : t('contacts.filter.channel')}
                      style={[styles.filterChip, compact && styles.filterChipCompact, selectedProvider ? styles.filterChipActive : null]}
                      accessibilityLabel={selectedProvider ? getProviderName(selectedProvider, t) : t('contacts.filter.channel')}
                      onPress={() => {
                        setShowAssignmentMenu(false);
                        if (showChannelMenu) {
                          setShowChannelMenu(false);
                        } else {
                          channelButtonRef.current?.measureInWindow((x, y, width, height) => {
                            setChannelMenuPosition({ x, y, width, height });
                            setShowChannelMenu(true);
                          });
                        }
                      }}
                    >
                      <Ionicons
                        name={selectedProvider ? getProviderIcon(selectedProvider) : 'grid-outline'}
                        size={compact ? 14 : 18}
                        color={selectedProvider ? colors.white : colors.primary}
                      />
                    </TouchableOpacity>
                    <View style={[styles.filterChipDivider, compact && styles.filterChipDividerCompact]} />
                    <TouchableOpacity
                      ref={assignmentButtonRef}
                      // @ts-ignore — title is a real DOM hover-tooltip attribute RNW forwards, not in the cross-platform type
                      title={t('contacts.filter.assignment')}
                      style={[styles.filterChip, compact && styles.filterChipCompact, (includeAssignmentFilters.length > 0 || excludeAssignmentFilters.length > 0) ? styles.filterChipActive : null]}
                      accessibilityLabel={t('contacts.filter.assignment')}
                      onPress={() => {
                        setShowChannelMenu(false);
                        setShowTagMenu(false);
                        assignmentButtonRef.current?.measureInWindow((x, y, width, height) => {
                          setAssignmentMenuPosition({ x, y, width, height });
                          setShowAssignmentMenu(true);
                          fetchAssignmentUsers();
                          fetchAssignmentDepartments();
                        });
                      }}
                    >
                      <Ionicons
                        name="person-circle-outline"
                        size={compact ? 14 : 18}
                        color={(includeAssignmentFilters.length > 0 || excludeAssignmentFilters.length > 0) ? colors.white : colors.primary}
                      />
                    </TouchableOpacity>

                    <>
                      <View style={[styles.filterChipDivider, compact && styles.filterChipDividerCompact]} />
                      <TouchableOpacity
                        ref={tagButtonRef}
                        // @ts-ignore — title is a real DOM hover-tooltip attribute RNW forwards, not in the cross-platform type
                        title={t('contacts.filter.tags')}
                        style={[styles.filterChip, compact && styles.filterChipCompact, { flexDirection: rtl.rowDirection }, (includeTags.length > 0 || excludeTags.length > 0) ? styles.filterChipActive : null]}
                        accessibilityLabel={t('contacts.filter.tags')}
                        onPress={() => {
                          setShowChannelMenu(false);
                          setShowAssignmentMenu(false);
                          if (showTagMenu) {
                            setShowTagMenu(false);
                          } else {
                            fetchTags();
                            tagButtonRef.current?.measureInWindow((x, y, width, height) => {
                              setTagMenuPosition({ x, y, width, height });
                              setShowTagMenu(true);
                            });
                          }
                        }}
                      >
                        <Ionicons name="pricetag-outline" size={compact ? 14 : 18} color={(includeTags.length > 0 || excludeTags.length > 0) ? colors.white : colors.primary} />
                        {(includeTags.length > 0 || excludeTags.length > 0) && (
                          <TouchableOpacity onPress={() => { setIncludeTags([]); setExcludeTags([]); setShowTagMenu(false); }} hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}>
                            <Ionicons name="close-circle" size={compact ? 11 : 13} color={colors.white} />
                          </TouchableOpacity>
                        )}
                      </TouchableOpacity>
                    </>

                    {availableContactDataLists.length > 0 && (
                      <>
                        <View style={[styles.filterChipDivider, compact && styles.filterChipDividerCompact]} />
                        <TouchableOpacity
                          ref={dataListButtonRef}
                          // @ts-ignore — title is a real DOM hover-tooltip attribute RNW forwards, not in the cross-platform type
                          title={t('contacts.filter.dataList')}
                          style={[styles.filterChip, compact && styles.filterChipCompact, { flexDirection: rtl.rowDirection }, (includeDataListIds.length > 0 || excludeDataListIds.length > 0) ? styles.filterChipActive : null]}
                          accessibilityLabel={t('contacts.filter.dataList')}
                          onPress={() => {
                            setShowChannelMenu(false);
                            setShowAssignmentMenu(false);
                            setShowTagMenu(false);
                            if (showDataListMenu) {
                              setShowDataListMenu(false);
                            } else {
                              dataListButtonRef.current?.measureInWindow((x, y, width, height) => {
                                setDataListMenuPosition({ x, y, width, height });
                                setShowDataListMenu(true);
                              });
                            }
                          }}
                        >
                          <Ionicons name="list-outline" size={compact ? 14 : 18} color={(includeDataListIds.length > 0 || excludeDataListIds.length > 0) ? colors.white : colors.primary} />
                          {(includeDataListIds.length > 0 || excludeDataListIds.length > 0) && (
                            <TouchableOpacity onPress={() => { setIncludeDataListIds([]); setExcludeDataListIds([]); setShowDataListMenu(false); }} hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}>
                              <Ionicons name="close-circle" size={compact ? 11 : 13} color={colors.white} />
                            </TouchableOpacity>
                          )}
                        </TouchableOpacity>
                      </>
                    )}

                    <View style={[styles.filterChipDivider, compact && styles.filterChipDividerCompact]} />
                    <TouchableOpacity
                      ref={locationButtonRef}
                      // @ts-ignore — title is a real DOM hover-tooltip attribute RNW forwards, not in the cross-platform type
                      title={t('contacts.filter.location.filterTitle')}
                      style={[
                        styles.filterChip, compact && styles.filterChipCompact,
                        { flexDirection: rtl.rowDirection },
                        (filterCities.length > 0 || filterCountries.length > 0 || (sortByDistance && nearPoint) || (radiusFilterEnabled && nearPoint)) ? styles.filterChipActive : null,
                      ]}
                      accessibilityLabel={t('contacts.filter.location.filterTitle')}
                      onPress={() => {
                        setShowChannelMenu(false);
                        setShowAssignmentMenu(false);
                        setShowTagMenu(false);
                        setShowDataListMenu(false);
                        if (showLocationMenu) {
                          setShowLocationMenu(false);
                        } else {
                          locationButtonRef.current?.measureInWindow((x, y, width, height) => {
                            setLocationMenuPosition({ x, y, width, height });
                            setShowLocationMenu(true);
                          });
                        }
                      }}
                    >
                      <Ionicons name="location-outline" size={compact ? 14 : 18} color={(filterCities.length > 0 || filterCountries.length > 0 || (sortByDistance && nearPoint) || (radiusFilterEnabled && nearPoint)) ? colors.white : colors.primary} />
                      {(filterCities.length > 0 || filterCountries.length > 0 || sortByDistance || radiusFilterEnabled) && (
                        <TouchableOpacity
                          onPress={() => { setFilterCities([]); setFilterCountries([]); setRadiusKm(''); setRadiusFilterEnabled(false); setSortByDistance(null); setNearPoint(null); setNearPointLabel(null); setShowLocationMenu(false); }}
                          hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
                        >
                          <Ionicons name="close-circle" size={compact ? 11 : 13} color={colors.white} />
                        </TouchableOpacity>
                      )}
                    </TouchableOpacity>

                    <View style={[styles.filterChipDivider, compact && styles.filterChipDividerCompact]} />
                    <TouchableOpacity
                      // @ts-ignore — title is a real DOM hover-tooltip attribute RNW forwards, not in the cross-platform type
                      title={t('contacts.sort.sortTitle')}
                      style={[
                        styles.filterChip, compact && styles.filterChipCompact,
                        { flexDirection: rtl.rowDirection },
                        (sortBy || Object.values(customFieldFilters).some(v => v.length > 0)) ? styles.filterChipActive : null,
                      ]}
                      accessibilityLabel={t('contacts.sort.sortTitle')}
                      onPress={() => {
                        setShowChannelMenu(false);
                        setShowAssignmentMenu(false);
                        setShowTagMenu(false);
                        setShowDataListMenu(false);
                        setShowLocationMenu(false);
                        setShowSortMenu(v => !v);
                      }}
                    >
                      <Ionicons name="swap-vertical-outline" size={compact ? 14 : 18} color={(sortBy || Object.values(customFieldFilters).some(v => v.length > 0)) ? colors.white : colors.primary} />
                      {(sortBy || Object.values(customFieldFilters).some(v => v.length > 0)) && (
                        <TouchableOpacity
                          onPress={() => { setSortBy(null); setSortOrder('asc'); setCustomFieldFilters({}); setShowSortMenu(false); }}
                          hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
                        >
                          <Ionicons name="close-circle" size={compact ? 11 : 13} color={colors.white} />
                        </TouchableOpacity>
                      )}
                    </TouchableOpacity>

                    <View style={[styles.filterChipDivider, compact && styles.filterChipDividerCompact]} />
                    {/* Real, parameterized analytical filters (a typed "no
                        reply in N days" threshold, a real order date-range)
                        — see ContactAnalyticalFiltersModal's own doc
                        comment. Its own icon/modal rather than folded into
                        Sort & Filter above, since it needs richer controls
                        (a number input, a calendar range picker) than that
                        modal's checklist/has-value-chip format supports. */}
                    <TouchableOpacity
                      // @ts-ignore — title is a real DOM hover-tooltip attribute RNW forwards, not in the cross-platform type
                      title={t('contacts.analyticalFilters.title')}
                      style={[
                        styles.filterChip, compact && styles.filterChipCompact,
                        { flexDirection: rtl.rowDirection },
                        countActiveAnalyticalFilters(analyticalFilters) > 0 ? styles.filterChipActive : null,
                      ]}
                      accessibilityLabel={t('contacts.analyticalFilters.title')}
                      onPress={() => {
                        setShowChannelMenu(false);
                        setShowAssignmentMenu(false);
                        setShowTagMenu(false);
                        setShowDataListMenu(false);
                        setShowLocationMenu(false);
                        setShowSortMenu(false);
                        setShowAnalyticalFiltersModal(true);
                      }}
                    >
                      <Ionicons name="analytics-outline" size={compact ? 14 : 18} color={countActiveAnalyticalFilters(analyticalFilters) > 0 ? colors.white : colors.primary} />
                      {countActiveAnalyticalFilters(analyticalFilters) > 0 && (
                        <TouchableOpacity
                          onPress={() => setAnalyticalFilters({})}
                          hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
                        >
                          <Ionicons name="close-circle" size={compact ? 11 : 13} color={colors.white} />
                        </TouchableOpacity>
                      )}
                    </TouchableOpacity>

                    <View style={[styles.filterChipDivider, compact && styles.filterChipDividerCompact]} />
                    <TouchableOpacity
                      ref={pageSizeButtonRef}
                      // @ts-ignore — title is a real DOM hover-tooltip attribute RNW forwards, not in the cross-platform type
                      title={t('contacts.filter.pageSize')}
                      style={[styles.filterChip, compact && styles.filterChipCompact, pageSize !== 50 && styles.filterChipActive]}
                      accessibilityLabel={t('contacts.filter.pageSize')}
                      onPress={() => {
                        setShowChannelMenu(false);
                        setShowAssignmentMenu(false);
                        setShowTagMenu(false);
                        setShowDataListMenu(false);
                        setShowLocationMenu(false);
                        setShowSortMenu(false);
                        if (showPageSizeMenu) {
                          setShowPageSizeMenu(false);
                        } else {
                          pageSizeButtonRef.current?.measureInWindow((x, y, width, height) => {
                            setPageSizeMenuPosition({ x, y, width, height });
                            setShowPageSizeMenu(true);
                          });
                        }
                      }}
                    >
                      <Ionicons name="layers-outline" size={compact ? 14 : 18} color={pageSize !== 50 ? colors.white : colors.primary} />
                    </TouchableOpacity>
                  </View>
                )}
              </>
            )}
          </View>
        </View>
      )}

      {/* Search Bar */}
      {showSearch && (
        <View style={[styles.searchContainer, { flexDirection: rtl.rowDirection }, compact && { marginVertical: spacing.sm }]}>
          <Ionicons name="search" size={16} color={colors.text.secondary} />
          <TextInput
            style={[styles.searchInput, { textAlign: rtl.textStart }]}
            placeholder={t('contacts.search.placeholder')}
            placeholderTextColor={colors.text.disabled}
            value={searchQuery}
            onChangeText={setInternalSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setInternalSearchQuery('')}>
              <Ionicons name="close-circle" size={16} color={colors.text.secondary} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Inbox status filter — server-side, like every other filter here */}
      {isInbox && (
        <View style={[styles.inboxStatusRow, { flexDirection: rtl.rowDirection }]}>
          {([
            { key: 'all', label: t('inbox.filter.all'), icon: 'apps' },
            { key: 'unread', label: t('inbox.filter.unread'), icon: 'mail-unread' },
            { key: 'active', label: t('inbox.filter.active'), icon: 'flash' },
            { key: 'near expiry', label: t('inbox.filter.nearExpiry'), icon: 'warning' },
            { key: 'expired', label: t('inbox.filter.expired'), icon: 'time-outline' },
          ] as const).map((option) => {
            const selected = inboxStatus === option.key;
            return (
              <TouchableOpacity
                key={option.key}
                style={[styles.filterChip, compact && styles.filterChipCompact, { flexDirection: rtl.rowDirection }, selected && styles.filterChipActive]}
                onPress={() => setInboxStatus(option.key)}
                accessibilityLabel={option.label}
              >
                <Ionicons name={option.icon as any} size={compact ? 14 : 16} color={selected ? colors.white : colors.primary} />
                {(selected || !compact) && (
                  <Text style={[styles.inboxStatusText, selected && styles.inboxStatusTextActive]} numberOfLines={1}>{option.label}</Text>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {/* Channel Filter Menu — previously a plain overflow:visible View with a
          hardcoded { top: 60, right: spacing.lg } position, which rendered far
          from the trigger button (position:absolute is relative to the nearest
          positioned ancestor, not the window, so window-relative measurements
          didn't line up) and never flipped side in RTL. Modal-based now,
          matching the Assignment menu below — Modal portals to the document
          root on web, so its content is always positioned relative to the
          window, matching measureInWindow's coordinate space. */}
      {showFilters && (
        <Modal
          transparent
          visible={showChannelMenu}
          animationType="fade"
          onRequestClose={() => setShowChannelMenu(false)}
        >
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => setShowChannelMenu(false)}
          >
            <View
              style={[
                styles.menuModalContainer,
                channelMenuPosition
                  ? menuAnchorStyle(channelMenuPosition, rtl.isRTL, 240)
                  : { top: 60, right: spacing.lg },
              ]}
              onStartShouldSetResponder={() => true}
            >
              <View style={[styles.channelMenu, { maxHeight: menuMaxHeight }]}>
                <ScrollView style={[styles.menuScroll, { maxHeight: menuMaxHeight }]} contentContainerStyle={styles.menuScrollContent}>
                  <TouchableOpacity
                    style={[
                      styles.channelMenuItem,
                      { flexDirection: rtl.rowDirection },
                      !selectedProvider && styles.channelMenuItemActive
                    ]}
                    onPress={() => {
                      setSelectedProvider(null);
                      setShowChannelMenu(false);
                    }}
                  >
                    <Ionicons name="grid-outline" size={18} color={!selectedProvider ? colors.white : colors.primary} />
                    <Text style={[
                      styles.channelMenuItemText,
                      !selectedProvider && styles.channelMenuItemTextActive
                    ]}>
                      {t('contacts.filter.allChannels')}
                    </Text>
                  </TouchableOpacity>

                  {providers.map(provider => (
                    <TouchableOpacity
                      key={provider}
                      style={[
                        styles.channelMenuItem,
                        { flexDirection: rtl.rowDirection },
                        selectedProvider === provider && styles.channelMenuItemActive
                      ]}
                      onPress={() => {
                        setSelectedProvider(provider);
                        setShowChannelMenu(false);
                      }}
                    >
                      <Ionicons
                        name={getProviderIcon(provider)}
                        size={18}
                        color={selectedProvider === provider ? colors.white : getProviderColor(provider)}
                      />
                      <Text style={[
                        styles.channelMenuItemText,
                        selectedProvider === provider && styles.channelMenuItemTextActive
                      ]}>
                        {getProviderName(provider, t)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </View>
          </TouchableOpacity>
        </Modal>
      )}

      {/* Page Size Menu — has its own TextInput, so its backdrop uses the
          DOM-containment check (not onStartShouldSetResponder, which doesn't
          reliably stop a click that focused the input from closing this
          menu) — same reasoning as the Tag menu above. */}
      {showFilters && (
        <Modal
          transparent
          visible={showPageSizeMenu}
          animationType="fade"
          onRequestClose={() => setShowPageSizeMenu(false)}
        >
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={(e: any) => {
              const target = e?.nativeEvent?.target || e?.target;
              if (target?.closest?.('#contacts-page-size-menu-content')) return;
              setShowPageSizeMenu(false);
            }}
          >
            <View
              // @ts-ignore — id is a real RNW View prop (maps to the DOM id attribute)
              id="contacts-page-size-menu-content"
              style={[
                styles.menuModalContainer,
                pageSizeMenuPosition
                  ? menuAnchorStyle(pageSizeMenuPosition, rtl.isRTL, 240)
                  : { top: 60, right: spacing.lg },
              ]}
            >
              <View style={styles.channelMenu}>
                <View style={styles.assignmentMenuSection}>
                  <Text style={styles.assignmentMenuSectionTitle}>{t('contacts.filter.pageSize')}</Text>
                </View>
                <View style={[styles.pageSizeRow, { flexDirection: rtl.rowDirection }]}>
                  <TextInput
                    style={styles.pageSizeInput}
                    value={pageSizeDraft}
                    onChangeText={setPageSizeDraft}
                    onBlur={commitPageSizeDraft}
                    onSubmitEditing={commitPageSizeDraft}
                    keyboardType="number-pad"
                    returnKeyType="done"
                  />
                  <Text style={[styles.assignmentMenuEmptyText, { textAlign: rtl.textStart, flex: 1 }]}>
                    {t('contacts.filter.pageSizeRange', { min: PAGE_SIZE_MIN })}
                  </Text>
                </View>
              </View>
            </View>
          </TouchableOpacity>
        </Modal>
      )}

      {/* Assignment Filter Menu (Modal) */}
      {showFilters && (
        <Modal
          transparent
          visible={showAssignmentMenu}
          animationType="fade"
          onRequestClose={() => setShowAssignmentMenu(false)}
        >
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => setShowAssignmentMenu(false)}
          >
            <View
              style={[
                styles.menuModalContainer,
                assignmentMenuPosition
                  ? menuAnchorStyle(assignmentMenuPosition, rtl.isRTL, 280)
                  : { top: 60, right: spacing.lg },
              ]}
            >
              <View style={[styles.channelMenu, { maxHeight: menuMaxHeight }]}>
                <ScrollView style={[styles.menuScroll, { maxHeight: menuMaxHeight }]} contentContainerStyle={styles.menuScrollContent}>
                  {(() => {
                    const hasAssignmentFilters = includeAssignmentFilters.length > 0 || excludeAssignmentFilters.length > 0;
                    const unassignedState = getAssignmentFilterState({ type: 'unassigned' });
                    return (
                      <>
                  <TouchableOpacity
                    style={[
                      styles.channelMenuItem,
                      !hasAssignmentFilters && styles.channelMenuItemActive
                    ]}
                    onPress={() => {
                      setIncludeAssignmentFilters([]);
                      setExcludeAssignmentFilters([]);
                      setShowAssignmentMenu(false);
                    }}
                  >
                    <Ionicons name="grid-outline" size={18} color={!hasAssignmentFilters ? colors.white : colors.primary} />
                    <Text style={[
                      styles.channelMenuItemText,
                      !hasAssignmentFilters && styles.channelMenuItemTextActive
                    ]}>
                      {t('contacts.filter.allAssignments')}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[
                      styles.channelMenuItem,
                      unassignedState && (unassignedState === 'exclude' ? styles.tagMenuItemExcluded : styles.channelMenuItemActive)
                    ]}
                    onPress={() => {
                      cycleAssignmentFilter({ type: 'unassigned' });
                    }}
                  >
                    <Ionicons
                      name={unassignedState === 'exclude' ? 'remove-circle' : 'person-outline'}
                      size={18}
                      color={unassignedState ? colors.white : colors.primary}
                    />
                    <Text style={[
                      styles.channelMenuItemText,
                      unassignedState && (unassignedState === 'exclude' ? styles.tagMenuItemExcludedText : styles.channelMenuItemTextActive)
                    ]}>
                      {t('contacts.filter.unassigned')}{unassignedState === 'exclude' ? t('contacts.filter.excludedSuffix') : ''}
                    </Text>
                  </TouchableOpacity>
                      </>
                    );
                  })()}

                  <View style={styles.assignmentMenuSection}>
                    <Text style={styles.assignmentMenuSectionTitle}>{t('contacts.filter.users')}</Text>
                  </View>
                  {assignmentUsersLoading ? (
                    <View style={styles.assignmentMenuLoading}>
                      <ActivityIndicator size="small" color={colors.primary} />
                      <Text style={styles.assignmentMenuLoadingText}>{t('contacts.filter.loadingUsers')}</Text>
                    </View>
                  ) : assignmentUsers.length === 0 ? (
                    <View style={styles.assignmentMenuEmpty}>
                      <Text style={styles.assignmentMenuEmptyText}>{t('contacts.filter.noUsers')}</Text>
                    </View>
                  ) : (
                    assignmentUsers.map(user => (
                      (() => {
                        const filterState = getAssignmentFilterState({ type: 'user', id: user._id });
                        const isCurrent = !!filterState;
                        const isExcluded = filterState === 'exclude';
                        return (
                      <TouchableOpacity
                        key={user._id}
                        style={[
                          styles.channelMenuItem,
                          isCurrent && (isExcluded ? styles.tagMenuItemExcluded : styles.channelMenuItemActive)
                        ]}
                        onPress={() => {
                          cycleAssignmentFilter({ type: 'user', id: user._id });
                        }}
                      >
                        <Ionicons name={isExcluded ? 'remove-circle' : 'person'} size={18} color={isCurrent ? colors.white : colors.primary} />
                        <Text style={[
                          styles.channelMenuItemText,
                          isCurrent && (isExcluded ? styles.tagMenuItemExcludedText : styles.channelMenuItemTextActive)
                        ]}>
                          {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || t('contacts.fallback.user')}{isExcluded ? t('contacts.filter.excludedSuffix') : ''}
                        </Text>
                      </TouchableOpacity>
                        );
                      })()
                    ))
                  )}

                  <View style={styles.assignmentMenuSection}>
                    <Text style={styles.assignmentMenuSectionTitle}>{t('contacts.filter.departments')}</Text>
                  </View>
                  {assignmentDepartmentsLoading ? (
                    <View style={styles.assignmentMenuLoading}>
                      <ActivityIndicator size="small" color={colors.primary} />
                      <Text style={styles.assignmentMenuLoadingText}>{t('contacts.filter.loadingDepartments')}</Text>
                    </View>
                  ) : assignmentDepartments.length === 0 ? (
                    <View style={styles.assignmentMenuEmpty}>
                      <Text style={styles.assignmentMenuEmptyText}>{t('contacts.filter.noDepartments')}</Text>
                    </View>
                  ) : (
                    assignmentDepartments.map(dept => (
                      (() => {
                        const filterState = getAssignmentFilterState({ type: 'department', id: dept._id });
                        const isCurrent = !!filterState;
                        const isExcluded = filterState === 'exclude';
                        return (
                      <TouchableOpacity
                        key={dept._id}
                        style={[
                          styles.channelMenuItem,
                          isCurrent && (isExcluded ? styles.tagMenuItemExcluded : styles.channelMenuItemActive)
                        ]}
                        onPress={() => {
                          cycleAssignmentFilter({ type: 'department', id: dept._id });
                        }}
                      >
                        <Ionicons name={isExcluded ? 'remove-circle' : 'people'} size={18} color={isCurrent ? colors.white : colors.primary} />
                        <Text style={[
                          styles.channelMenuItemText,
                          isCurrent && (isExcluded ? styles.tagMenuItemExcludedText : styles.channelMenuItemTextActive)
                        ]}>
                          {dept.name}{isExcluded ? t('contacts.filter.excludedSuffix') : ''}
                        </Text>
                      </TouchableOpacity>
                        );
                      })()
                    ))
                  )}
                </ScrollView>
              </View>
            </View>
          </TouchableOpacity>
        </Modal>
      )}

      {/* Tag Filter Dropdown — tap a tag to cycle: neutral -> include (must have) ->
          exclude (must NOT have) -> neutral. Menu stays open across taps so multiple
          tags can be set in one pass, unlike the old single-select version. */}
      {showFilters && (
        <Modal
          transparent
          visible={showTagMenu}
          animationType="fade"
          onRequestClose={() => { setShowTagMenu(false); setTagFilterSearch(''); }}
        >
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            // DOM-containment check, not onStartShouldSetResponder — the latter
            // doesn't reliably stop a click that focused this menu's own tag
            // search TextInput from closing the menu (RN's responder system
            // doesn't fully cover real DOM <input> elements on web). See the
            // combined Filters menu's own backdrop for the full explanation.
            onPress={(e: any) => {
              const target = e?.nativeEvent?.target || e?.target;
              if (target?.closest?.('#contacts-tag-menu-content')) return;
              setShowTagMenu(false);
              setTagFilterSearch('');
            }}
          >
            <View
              // @ts-ignore — id is a real RNW View prop (maps to the DOM id attribute)
              id="contacts-tag-menu-content"
              style={[
                styles.menuModalContainer,
                tagMenuPosition
                  ? menuAnchorStyle(tagMenuPosition, rtl.isRTL, 240)
                  : { top: 60, right: spacing.lg },
              ]}
              onStartShouldSetResponder={() => true}
            >
            <View style={styles.channelMenu}>
              <View style={[styles.tagSearchContainer, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="search" size={14} color={colors.text.secondary} />
                <TextInput
                  style={[styles.tagSearchInput, { textAlign: rtl.textStart }]}
                  placeholder={t('contacts.filter.searchTagsPlaceholder')}
                  placeholderTextColor={colors.text.disabled}
                  value={tagFilterSearch}
                  onChangeText={setTagFilterSearch}
                  autoCapitalize="none"
                />
                {tagFilterSearch.length > 0 && (
                  <TouchableOpacity onPress={() => setTagFilterSearch('')}>
                    <Ionicons name="close-circle" size={14} color={colors.text.secondary} />
                  </TouchableOpacity>
                )}
              </View>
              {(includeTags.length > 0 || excludeTags.length > 0) && (
                <TouchableOpacity
                  style={[styles.channelMenuItem, { flexDirection: rtl.rowDirection }]}
                  onPress={() => { setIncludeTags([]); setExcludeTags([]); }}
                >
                  <Ionicons name="pricetags-outline" size={18} color={colors.primary} />
                  <Text style={styles.channelMenuItemText}>{t('contacts.filter.clearTagFilters')}</Text>
                </TouchableOpacity>
              )}
              <ScrollView style={[styles.menuScroll, { maxHeight: menuMaxHeight }]} contentContainerStyle={styles.menuScrollContent}>
                {availableTags.filter(tag => tag.toLowerCase().includes(tagFilterSearch.trim().toLowerCase())).length === 0 && (
                  <Text style={styles.tagSearchEmptyText}>{t('contacts.filter.noTagsMatch')}</Text>
                )}
                {availableTags.filter(tag => tag.toLowerCase().includes(tagFilterSearch.trim().toLowerCase())).map(tag => {
                  const isIncluded = includeTags.includes(tag);
                  const isExcluded = excludeTags.includes(tag);
                  const cycleTag = () => {
                    if (!isIncluded && !isExcluded) {
                      setIncludeTags(prev => [...prev, tag]);
                    } else if (isIncluded) {
                      setIncludeTags(prev => prev.filter(t => t !== tag));
                      setExcludeTags(prev => [...prev, tag]);
                    } else {
                      setExcludeTags(prev => prev.filter(t => t !== tag));
                    }
                  };
                  const iconName = isExcluded ? 'remove-circle' : isIncluded ? 'checkmark-circle' : 'pricetag-outline';
                  const iconColor = isExcluded ? colors.error : isIncluded ? colors.white : colors.primary;
                  return (
                    <TouchableOpacity
                      key={tag}
                      style={[
                        styles.channelMenuItem,
                        { flexDirection: rtl.rowDirection },
                        isIncluded && styles.channelMenuItemActive,
                        isExcluded && styles.tagMenuItemExcluded,
                      ]}
                      onPress={cycleTag}
                    >
                      <Ionicons name={iconName as any} size={18} color={iconColor} />
                      <Text style={[
                        styles.channelMenuItemText,
                        isIncluded && styles.channelMenuItemTextActive,
                        isExcluded && styles.tagMenuItemExcludedText,
                      ]}>
                        {tag}{isExcluded ? t('contacts.filter.excludedSuffix') : ''}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
            </View>
          </TouchableOpacity>
        </Modal>
      )}

      {/* Data List Filter Dropdown — restricts the contact list to only contacts
          added THROUGH a chosen System Datalist (contacts module), server-side
          via dataListId (see OmniContactService.list). */}
      {showFilters && availableContactDataLists.length > 0 && (
        <Modal
          transparent
          visible={showDataListMenu}
          animationType="fade"
          onRequestClose={() => setShowDataListMenu(false)}
        >
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => setShowDataListMenu(false)}
          >
            <View
              style={[
                styles.menuModalContainer,
                dataListMenuPosition
                  ? menuAnchorStyle(dataListMenuPosition, rtl.isRTL, 240)
                  : { top: 60, right: spacing.lg },
              ]}
              onStartShouldSetResponder={() => true}
            >
            <View style={[styles.channelMenu, { maxHeight: menuMaxHeight }]}>
              <ScrollView style={[styles.menuScroll, { maxHeight: menuMaxHeight }]} contentContainerStyle={styles.menuScrollContent}>
                {(includeDataListIds.length > 0 || excludeDataListIds.length > 0) && (
                  <TouchableOpacity
                    style={[styles.channelMenuItem, { flexDirection: rtl.rowDirection }]}
                    onPress={() => { setIncludeDataListIds([]); setExcludeDataListIds([]); }}
                  >
                    <Ionicons name="list-outline" size={18} color={colors.primary} />
                    <Text style={styles.channelMenuItemText}>{t('contacts.filter.allDataLists')}</Text>
                  </TouchableOpacity>
                )}
                {availableContactDataLists.map(list => {
                  const isIncluded = includeDataListIds.includes(list._id);
                  const isExcluded = excludeDataListIds.includes(list._id);
                  const cycleList = () => {
                    if (!isIncluded && !isExcluded) {
                      setIncludeDataListIds(prev => [...prev, list._id]);
                    } else if (isIncluded) {
                      setIncludeDataListIds(prev => prev.filter(id => id !== list._id));
                      setExcludeDataListIds(prev => [...prev, list._id]);
                    } else {
                      setExcludeDataListIds(prev => prev.filter(id => id !== list._id));
                    }
                  };
                  const iconName = isExcluded ? 'remove-circle' : isIncluded ? 'checkmark-circle' : 'list-outline';
                  const iconColor = isExcluded ? colors.error : isIncluded ? colors.white : colors.primary;
                  return (
                    <TouchableOpacity
                      key={list._id}
                      style={[styles.channelMenuItem, { flexDirection: rtl.rowDirection }, isIncluded && styles.channelMenuItemActive, isExcluded && styles.tagMenuItemExcluded]}
                      onPress={cycleList}
                    >
                      <Ionicons name={iconName as any} size={18} color={iconColor} />
                      <Text style={[styles.channelMenuItemText, isIncluded && styles.channelMenuItemTextActive, isExcluded && styles.tagMenuItemExcludedText]} numberOfLines={1}>
                        {list.name}{isExcluded ? t('contacts.filter.excludedSuffix') : ''}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
            </View>
          </TouchableOpacity>
        </Modal>
      )}

      {/* Location Filter/Sort Menu — city/country exact match + radius
          around the sort-by-distance point, plus the sort itself
          (nearest/farthest from either the picked point or the viewer's
          own current position). Server-side (see OmniContactService.list's
          filterCity/filterCountry/nearLatitude/nearLongitude/radiusKm/
          sortByDistance), backed by the 2dsphere index. Previously
          desktop-only (!compact) — opened only from the individual
          per-field toolbar button, now disabled everywhere in favor of the
          combined "Filters" menu's own Location row below, so this must
          render regardless of compact/full mode too. That row seeds
          locationMenuPosition from the Filters button's own measured
          position before opening this modal, so it still anchors sensibly
          instead of falling back to a fixed top-right placement unrelated
          to whatever was actually clicked. */}
      {showFilters && (
        <Modal
          transparent
          visible={showLocationMenu}
          animationType="fade"
          onRequestClose={() => setShowLocationMenu(false)}
        >
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            // Same DOM-containment check as the combined Filters menu's backdrop
            // (see its comment) — this menu's own city/country search
            // TextInputs are exposed to the identical risk.
            onPress={(e: any) => {
              const target = e?.nativeEvent?.target || e?.target;
              if (target?.closest?.('#contacts-location-menu-content')) return;
              setShowLocationMenu(false);
            }}
          >
            <TouchableOpacity
              activeOpacity={1}
              onPress={(e) => e.stopPropagation()}
              // @ts-ignore — id is a real RNW View prop (maps to the DOM id attribute)
              id="contacts-location-menu-content"
              style={[
                styles.menuModalContainer,
                locationMenuPosition
                  ? menuAnchorStyle(locationMenuPosition, rtl.isRTL, 280)
                  : { top: 60, right: spacing.lg },
              ]}
            >
              <View style={[styles.channelMenu, { maxHeight: menuMaxHeight }]}>
              <View style={styles.locationMenuScroll}>
                <Text style={[styles.assignmentMenuSectionTitle, { textAlign: rtl.textStart }]}>{t('contacts.filter.location.filterTitle')}</Text>
                <TouchableOpacity
                  style={[styles.locationFilterPickerRow, { flexDirection: rtl.rowDirection }]}
                  onPress={() => { setShowCityMenu(v => !v); setShowCountryMenu(false); }}
                >
                  <Text style={[styles.locationFilterPickerText, { textAlign: rtl.textStart }, filterCities.length === 0 && styles.locationFilterPickerPlaceholder]} numberOfLines={1}>
                    {filterCities.length > 0 ? filterCities.join(', ') : t('contacts.filter.location.cityPlaceholder')}
                  </Text>
                  <Ionicons name={showCityMenu ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {showCityMenu && (
                  <View style={styles.locationCityList}>
                    <View style={[styles.tagSearchContainer, { flexDirection: rtl.rowDirection }]}>
                      <Ionicons name="search" size={14} color={colors.text.secondary} />
                      <TextInput
                        style={[styles.tagSearchInput, { textAlign: rtl.textStart }]}
                        placeholder={t('contacts.filter.searchTagsPlaceholder')}
                        placeholderTextColor={colors.text.disabled}
                        value={cityFilterSearch}
                        onChangeText={setCityFilterSearch}
                        autoCapitalize="none"
                      />
                    </View>
                    <View style={styles.locationInnerScrollList}>
                      {filterCities.length > 0 && (
                        <TouchableOpacity style={styles.locationCityItem} onPress={() => setFilterCities([])}>
                          <Text style={styles.locationCityItemText}>{t('contacts.filter.location.anyCity')}</Text>
                        </TouchableOpacity>
                      )}
                      {availableCities.filter(city => city.toLowerCase().includes(cityFilterSearch.trim().toLowerCase())).length === 0 && (
                        <Text style={styles.locationCityEmptyText}>{t('contacts.filter.location.noCities')}</Text>
                      )}
                      {availableCities
                        .filter(city => city.toLowerCase().includes(cityFilterSearch.trim().toLowerCase()))
                        .map(city => {
                          const isSelected = filterCities.includes(city);
                          return (
                            <TouchableOpacity
                              key={city}
                              style={[styles.locationCityItem, { flexDirection: rtl.rowDirection }, isSelected && styles.channelMenuItemActive]}
                              onPress={() => setFilterCities(prev => isSelected ? prev.filter(c => c !== city) : [...prev, city])}
                            >
                              <Text style={[styles.locationCityItemText, isSelected && styles.channelMenuItemTextActive]}>{city}</Text>
                              {isSelected && <Ionicons name="checkmark" size={16} color={colors.white} />}
                            </TouchableOpacity>
                          );
                        })}
                    </View>
                  </View>
                )}

                <TouchableOpacity
                  style={[styles.locationFilterPickerRow, { flexDirection: rtl.rowDirection, marginTop: spacing.sm }]}
                  onPress={() => { setShowCountryMenu(v => !v); setShowCityMenu(false); }}
                >
                  <Text style={[styles.locationFilterPickerText, { textAlign: rtl.textStart }, filterCountries.length === 0 && styles.locationFilterPickerPlaceholder]} numberOfLines={1}>
                    {filterCountries.length > 0 ? filterCountries.join(', ') : t('contacts.filter.location.countryPlaceholder')}
                  </Text>
                  <Ionicons name={showCountryMenu ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
                </TouchableOpacity>
                {showCountryMenu && (
                  <View style={styles.locationCityList}>
                    <View style={[styles.tagSearchContainer, { flexDirection: rtl.rowDirection }]}>
                      <Ionicons name="search" size={14} color={colors.text.secondary} />
                      <TextInput
                        style={[styles.tagSearchInput, { textAlign: rtl.textStart }]}
                        placeholder={t('contacts.filter.searchTagsPlaceholder')}
                        placeholderTextColor={colors.text.disabled}
                        value={countryFilterSearch}
                        onChangeText={setCountryFilterSearch}
                        autoCapitalize="none"
                      />
                    </View>
                    <View style={styles.locationInnerScrollList}>
                      {filterCountries.length > 0 && (
                        <TouchableOpacity style={styles.locationCityItem} onPress={() => setFilterCountries([])}>
                          <Text style={styles.locationCityItemText}>{t('contacts.filter.location.anyCountry')}</Text>
                        </TouchableOpacity>
                      )}
                      {countries
                        .filter(c => c.name.toLowerCase().includes(countryFilterSearch.trim().toLowerCase()))
                        .map(country => {
                          const isSelected = filterCountries.includes(country.name);
                          return (
                            <TouchableOpacity
                              key={country.code}
                              style={[styles.locationCityItem, { flexDirection: rtl.rowDirection }, isSelected && styles.channelMenuItemActive]}
                              onPress={() => setFilterCountries(prev => isSelected ? prev.filter(c => c !== country.name) : [...prev, country.name])}
                            >
                              <Text style={styles.flagEmoji}>{country.flag}</Text>
                              <Text style={[styles.locationCityItemText, isSelected && styles.channelMenuItemTextActive]}>{country.name}</Text>
                              {isSelected && <Ionicons name="checkmark" size={16} color={colors.white} />}
                            </TouchableOpacity>
                          );
                        })}
                    </View>
                  </View>
                )}

                <Text style={[styles.assignmentMenuSectionTitle, { textAlign: rtl.textStart, marginTop: spacing.sm }]}>{t('contacts.filter.location.sortTitle')}</Text>
                <View style={[styles.locationFilterRow, { flexDirection: rtl.rowDirection }]}>
                  <TouchableOpacity
                    style={[styles.locationSortButton, sortByDistance === 'nearest' && styles.channelMenuItemActive]}
                    onPress={() => {
                      // A map-picked point already IS the reference — re-running
                      // handleUseMyLocation here would silently replace it with
                      // the device's own GPS position, contradicting the button's
                      // "to me" label. Only fetch GPS when there's no point yet.
                      if (nearPointLabel && nearPoint) {
                        setSortByDistance('nearest');
                      } else {
                        handleUseMyLocation('nearest');
                      }
                    }}
                    disabled={locatingUser}
                  >
                    {locatingUser && sortByDistance !== 'farthest' ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : (
                      <Ionicons name="navigate-outline" size={16} color={sortByDistance === 'nearest' ? colors.white : colors.primary} />
                    )}
                    <Text style={[styles.locationSortButtonText, sortByDistance === 'nearest' && styles.channelMenuItemTextActive]}>
                      {t('contacts.filter.location.nearest')}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.locationSortButton, sortByDistance === 'farthest' && styles.channelMenuItemActive]}
                    onPress={() => {
                      if (nearPointLabel && nearPoint) {
                        setSortByDistance('farthest');
                      } else {
                        handleUseMyLocation('farthest');
                      }
                    }}
                    disabled={locatingUser}
                  >
                    {locatingUser && sortByDistance !== 'nearest' ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : (
                      <Ionicons name="navigate-outline" size={16} color={sortByDistance === 'farthest' ? colors.white : colors.primary} />
                    )}
                    <Text style={[styles.locationSortButtonText, sortByDistance === 'farthest' && styles.channelMenuItemTextActive]}>
                      {t('contacts.filter.location.farthest')}
                    </Text>
                  </TouchableOpacity>
                </View>
                <TouchableOpacity
                  style={[styles.locationFilterPickerRow, { flexDirection: rtl.rowDirection, marginTop: spacing.xs }]}
                  onPress={() => {
                    // Close this menu's own Modal before opening
                    // MapLocationPicker's — two stacked Modals fought over
                    // touch capture on web, which was reaching the map
                    // picker's search box through this menu's still-open
                    // backdrop and never actually landing on the input.
                    setShowLocationMenu(false);
                    setShowMapPointPicker(true);
                  }}
                >
                  <Ionicons name="map-outline" size={16} color={colors.primary} />
                  <Text style={[styles.locationFilterPickerText, { textAlign: rtl.textStart }, !nearPointLabel && styles.locationFilterPickerPlaceholder]} numberOfLines={1}>
                    {nearPointLabel || t('contacts.filter.location.pickOnMap')}
                  </Text>
                </TouchableOpacity>
                {nearPoint && (
                  // Independent of Nearest/Farthest above — a radius
                  // filter restricts WHICH contacts are considered at all,
                  // in either sort direction (or with no sort direction
                  // picked, as a plain "only show nearby contacts" filter).
                  // It used to only appear alongside a chosen sort
                  // direction and applied unconditionally whenever radiusKm
                  // had a value — which silently broke "Farthest" whenever
                  // a radius was still filled in from an earlier search
                  // (it restricted to WITHIN that radius first, then sorted
                  // that tiny leftover set descending).
                  <View style={[styles.radiusFilterRow, { flexDirection: rtl.rowDirection }]}>
                    <Switch
                      value={radiusFilterEnabled}
                      onValueChange={setRadiusFilterEnabled}
                      trackColor={{ false: colors.border, true: colors.primary }}
                    />
                    <TextInput
                      style={[styles.locationFilterInput, styles.radiusFilterInput, { textAlign: rtl.textStart }, !radiusFilterEnabled && styles.locationFilterPickerPlaceholder]}
                      placeholder={t('contacts.filter.location.radiusPlaceholder')}
                      placeholderTextColor={colors.text.disabled}
                      value={radiusKm}
                      onChangeText={setRadiusKm}
                      keyboardType="numeric"
                      editable={radiusFilterEnabled}
                    />
                  </View>
                )}

                {nearPoint && (
                  // Contacts with only typed city/address/country and no
                  // dropped pin have no coordinate to measure a distance
                  // from at all — on by default so they still show up
                  // (server backfills their coordinates from that text in
                  // the background) instead of silently vanishing from a
                  // distance query.
                  <View style={[styles.includeUnlocatedRow, { flexDirection: rtl.rowDirection }]}>
                    <Text style={[styles.includeUnlocatedText, { textAlign: rtl.textStart }]}>
                      {t('contacts.filter.location.includeUnlocated')}
                    </Text>
                    <Switch
                      value={includeUnlocated}
                      onValueChange={setIncludeUnlocated}
                      trackColor={{ false: colors.border, true: colors.primary }}
                    />
                  </View>
                )}

                <TouchableOpacity
                  style={styles.locationApplyButton}
                  onPress={() => setShowLocationMenu(false)}
                >
                  <Text style={styles.locationApplyButtonText}>{t('contacts.filter.location.apply')}</Text>
                </TouchableOpacity>
              </View>
              </View>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      )}

      {/* Sort-by-field + filter-by-value — full-screen (see
          ContactSortFilterModal's own doc comment for why this isn't a
          small anchored dropdown like the location filter above: 8 fixed
          sort fields, several of which are ALSO filterable via a
          searchable seen-values checklist, plus a per-business, potentially
          long list of custom fields with the same checklist, needs real
          room and only one expanded section at a time). Server-side
          (OmniContactService.list's sortBy/sortOrder/customFieldFilters —
          the latter covers both fixed-field and custom-field filters). */}
      {showFilters && (
        <ContactSortFilterModal
          visible={showSortMenu}
          onClose={() => setShowSortMenu(false)}
          sortFields={CONTACT_SORT_FIELDS}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onChangeSort={(field, order) => { setSortBy(field); setSortOrder(order); }}
          customFieldKeys={customFieldKeys}
          customFieldFilters={customFieldFilters}
          onChangeCustomFieldFilters={setCustomFieldFilters}
          fieldValueOptions={customFieldValueOptions}
          loadingFieldValues={loadingCustomFieldValues}
          onExpandField={(fieldId, isFixedField) => {
            // All hasValueOnly fields — no literal value list to fetch, only
            // the Has/No-value chips apply.
            if (fieldId === 'location' || fieldId === 'hasChannels') return;
            if (!customFieldValueOptions[fieldId]) fetchFieldValues(fieldId, isFixedField);
          }}
        />
      )}

      {showFilters && (
        <ContactAnalyticalFiltersModal
          visible={showAnalyticalFiltersModal}
          value={analyticalFilters}
          onApply={setAnalyticalFilters}
          onClose={() => setShowAnalyticalFiltersModal(false)}
          availableChannels={providers}
        />
      )}

      {/* Reference point for "measure from" distance sort/radius — either
          the viewer's own current position (handleUseMyLocation) or a point
          they tap on the map here. Defaults the sort direction to nearest
          when picked this way, matching "show me who's closest to this
          spot" as the natural read of dropping a pin — Nearest/Farthest
          above still switch direction afterward without re-picking. */}
      {showMapPointPicker && (
        <MapLocationPicker
          visible={showMapPointPicker}
          onClose={() => { setShowMapPointPicker(false); setShowLocationMenu(true); }}
          onLocationSelect={(loc) => {
            setNearPoint({ latitude: loc.latitude, longitude: loc.longitude });
            setNearPointLabel(loc.address);
            setSortByDistance(prev => prev || 'nearest');
            setRadiusKm(prev => prev || '5');
            setShowMapPointPicker(false);
            setShowLocationMenu(true);
          }}
        />
      )}

      {/* Contacts List — virtualized via FlatList (was ScrollView + nested .map(),
          which mounted every loaded contact into the native tree regardless of
          visibility). Section headers and contact rows are both items in the same
          flattened array (see flatListItems above) since FlatList needs one flat
          data source. */}
      {/* Visible feedback while a filter/sort/search change is re-fetching —
          previously the ONLY loading feedback for that case was the
          FlatList's native RefreshControl (refreshing state), which has no
          visible spinner on web (no native pull-to-refresh gesture there).
          Applying a filter on web therefore looked like it silently did
          nothing until the new results popped in. This bar is plain
          View/ActivityIndicator, so it renders identically on every
          platform. */}
      {refreshing && !loading && (
        <View style={[styles.refreshingBar, { flexDirection: rtl.rowDirection }]}>
          <ActivityIndicator size="small" color={colors.primary} />
          <Text style={styles.refreshingBarText}>{t('contacts.empty.updating')}</Text>
        </View>
      )}

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>{t('contacts.empty.loading')}</Text>
        </View>
      ) : !hasConnectedChannel && filteredContacts.length === 0 && !searchQuery ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="chatbubbles-outline" size={48} color={colors.text.secondary} />
          <Text style={styles.emptyTitle}>{t('contacts.empty.noConfiguredChannels')}</Text>
          <Text style={styles.emptySubtitle}>
            {t('contacts.empty.noConfiguredChannelsHint')}
          </Text>
        </View>
      ) : filteredContacts.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="people-outline" size={48} color={colors.text.secondary} />
          <Text style={styles.emptyTitle}>
            {searchQuery ? t('contacts.empty.noResultsFound') : t(isInbox ? 'inbox.empty' : 'contacts.empty.noContactsYet')}
          </Text>
          <Text style={styles.emptySubtitle}>
            {searchQuery
              ? t('contacts.empty.tryAdjusting')
              : t('contacts.empty.willAppear')
            }
          </Text>
        </View>
      ) : (
        <FlatList
          style={styles.contactsList}
          contentContainerStyle={scrollContentPaddingBottom ? { paddingBottom: scrollContentPaddingBottom } : undefined}
          showsVerticalScrollIndicator={false}
          data={flatListItems}
          keyExtractor={(item) => item.key}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          }
          onEndReached={() => {
            if (hasMoreContacts && !loadingMore) {
              loadMoreContacts();
            }
          }}
          onEndReachedThreshold={0.3}
          // The tags popover is a Modal positioned once from the tap point —
          // it doesn't scroll with the card underneath it, so close it as
          // soon as the list moves instead of leaving it hovering over the
          // wrong row.
          onScrollBeginDrag={() => {
            if (tagsPopoverContact) setTagsPopoverContact(null);
          }}
          // Virtualization tuning: render a modest window around the viewport
          // instead of everything, and drop offscreen rows from the native tree.
          windowSize={7}
          maxToRenderPerBatch={20}
          updateCellsBatchingPeriod={50}
          initialNumToRender={20}
          removeClippedSubviews={Platform.OS !== 'web'}
          renderItem={({ item }) => {
            return (
              <ContactRow
                contact={item.contact}
                isSelected={selectedSet.has(item.contact.id)}
                isActive={!!activeContactId && item.contact.id === activeContactId}
                selectionMode={selectionMode}
                canManageContactAssignments={canManageContactAssignments}
                apiBaseURL={apiBaseURL}
                compact={compact}
                onPress={handleContactPress}
                onAssignmentPress={handleAssignmentPress}
                onTagsPress={handleTagsPress}
                getAssignmentLabels={getAssignmentLabels}
                formatAssignmentLabel={formatAssignmentLabel}
              />
            );
          }}
          ListFooterComponent={
            <>
              {loadingMore && (
                <View style={[styles.loadingMoreContainer, { flexDirection: rtl.rowDirection }]}>
                  <ActivityIndicator size="small" color={colors.primary} />
                  <Text style={styles.loadingMoreText}>{t('contacts.loadingMore')}</Text>
                </View>
              )}
              {!loadingMore && hasMoreContacts && Platform.OS === 'web' && (
                <TouchableOpacity style={styles.loadMoreButton} onPress={loadMoreContacts}>
                  <Text style={styles.loadMoreButtonText}>
                    {t('contacts.loadMore')} ({totalCount - filteredContacts.length > 0 ? t('contacts.loadMoreRemaining', { count: totalCount - filteredContacts.length }) : t('contacts.loadMore').toLowerCase()})
                  </Text>
                </TouchableOpacity>
              )}
            </>
          }
        />
      )}

      {/* Assignment Modal */}
      {selectedContactForAssignment && (
        <UserAssignmentModal
          visible={assignmentModalVisible}
          onClose={() => {
            setAssignmentModalVisible(false);
            setSelectedContactForAssignment(null);
          }}
          contactId={selectedContactForAssignment.id}
          parentContactId={selectedContactForAssignment.parentContact?._id || selectedContactForAssignment.parentContact?.id}
          contactName={selectedContactForAssignment.name}
          currentAssignment={selectedContactForAssignment.assignment || null}
          onAssignmentChange={handleAssignmentChange}
        />
      )}

      {/* Bulk Assign Modal */}
      <Modal
        visible={bulkAssignVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setBulkAssignVisible(false)}
      >
        <TouchableOpacity
          style={styles.assignmentModalOverlay}
          onPress={() => setBulkAssignVisible(false)}
          activeOpacity={1}
        >
          <View
            style={styles.bulkAssignModal}
            onStartShouldSetResponder={() => true}
          >
            <Text style={[styles.bulkAssignTitle, { textAlign: rtl.textStart }]}>{t('contacts.bulkAssign.title')}</Text>
            <Text style={[styles.bulkAssignSubtitle, { textAlign: rtl.textStart }]}>{t('contacts.header.selectedCount', { count: selectedSet.size })}</Text>

            <View style={[styles.bulkAssignModeToggle, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={[styles.bulkAssignModeButton, bulkAssignMode === 'assign' && styles.bulkAssignModeButtonActive]}
                onPress={() => setBulkAssignMode('assign')}
                disabled={bulkAssigning}
              >
                <Text style={[styles.bulkAssignModeButtonText, bulkAssignMode === 'assign' && styles.bulkAssignModeButtonTextActive]}>
                  {t('contacts.bulkAssign.assignTab')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.bulkAssignModeButton, bulkAssignMode === 'unassign' && styles.bulkAssignModeButtonActive]}
                onPress={() => setBulkAssignMode('unassign')}
                disabled={bulkAssigning}
              >
                <Text style={[styles.bulkAssignModeButtonText, bulkAssignMode === 'unassign' && styles.bulkAssignModeButtonTextActive]}>
                  {t('contacts.bulkAssign.unassignTab')}
                </Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.bulkAssignList}>
              <Text style={styles.assignmentMenuSectionTitle}>{t('contacts.filter.users')}</Text>
              {assignmentUsersLoading ? (
                <View style={[styles.assignmentMenuLoading, { flexDirection: rtl.rowDirection }]}>
                  <ActivityIndicator size="small" color={colors.primary} />
                  <Text style={styles.assignmentMenuLoadingText}>{t('contacts.filter.loadingUsers')}</Text>
                </View>
              ) : assignmentUsers.length === 0 ? (
                <View style={styles.assignmentMenuEmpty}>
                  <Text style={styles.assignmentMenuEmptyText}>{t('contacts.filter.noUsers')}</Text>
                </View>
              ) : (
                assignmentUsers.map(user => (
                  <TouchableOpacity
                    key={user._id}
                    style={[styles.bulkAssignItem, { flexDirection: rtl.rowDirection }]}
                    onPress={() => (bulkAssignMode === 'assign' ? handleBulkAssign : handleBulkUnassign)('user', user._id)}
                    disabled={bulkAssigning}
                  >
                    <Ionicons name={bulkAssignMode === 'assign' ? 'person-outline' : 'person-remove-outline'} size={16} color={colors.primary} />
                    <Text style={styles.bulkAssignItemText}>
                      {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                    </Text>
                  </TouchableOpacity>
                ))
              )}

              <Text style={[styles.assignmentMenuSectionTitle, styles.bulkAssignSectionSpacing]}>
                {t('contacts.filter.departments')}
              </Text>
              {assignmentDepartmentsLoading ? (
                <View style={[styles.assignmentMenuLoading, { flexDirection: rtl.rowDirection }]}>
                  <ActivityIndicator size="small" color={colors.primary} />
                  <Text style={styles.assignmentMenuLoadingText}>{t('contacts.filter.loadingDepartments')}</Text>
                </View>
              ) : assignmentDepartments.length === 0 ? (
                <View style={styles.assignmentMenuEmpty}>
                  <Text style={styles.assignmentMenuEmptyText}>{t('contacts.filter.noDepartments')}</Text>
                </View>
              ) : (
                assignmentDepartments.map(dept => (
                  <TouchableOpacity
                    key={dept._id}
                    style={[styles.bulkAssignItem, { flexDirection: rtl.rowDirection }]}
                    onPress={() => (bulkAssignMode === 'assign' ? handleBulkAssign : handleBulkUnassign)('department', dept._id)}
                    disabled={bulkAssigning}
                  >
                    <Ionicons name="people-outline" size={16} color={colors.primary} />
                    <Text style={styles.bulkAssignItemText}>{dept.name}</Text>
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>

            <TouchableOpacity
              style={styles.bulkAssignCancel}
              onPress={() => setBulkAssignVisible(false)}
              disabled={bulkAssigning}
            >
              <Text style={styles.bulkAssignCancelText}>{t('contacts.bulkAssign.close')}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Bulk Tag Modal — mirrors the Bulk Assign modal's structure/styling. Supports
          adding OR removing multiple tags (comma/newline-separated) across every
          selected contact in one action, which previously required editing contacts
          one at a time. */}
      <Modal
        visible={bulkTagVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setBulkTagVisible(false)}
      >
        <TouchableOpacity
          style={styles.assignmentModalOverlay}
          // DOM-containment check (see the combined Filters menu's backdrop for
          // why not onStartShouldSetResponder or e.target/currentTarget) — this
          // modal now has a real TextInput too (the remove-mode tag search below).
          onPress={(e: any) => {
            const target = e?.nativeEvent?.target || e?.target;
            if (target?.closest?.('#contacts-bulk-tag-modal-content')) return;
            setBulkTagVisible(false);
          }}
          activeOpacity={1}
        >
          <View
            style={styles.bulkAssignModal}
            // @ts-ignore — id is a real RNW View prop (maps to the DOM id attribute)
            id="contacts-bulk-tag-modal-content"
          >
            <Text style={[styles.bulkAssignTitle, { textAlign: rtl.textStart }]}>{t('contacts.bulkTag.title')}</Text>
            <Text style={[styles.bulkAssignSubtitle, { textAlign: rtl.textStart }]}>{t('contacts.header.selectedCount', { count: selectedSet.size })}</Text>

            <View style={[styles.bulkTagActionRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={[styles.bulkTagActionButton, { flexDirection: rtl.rowDirection }, bulkTagAction === 'add' && styles.bulkTagActionButtonActive]}
                onPress={() => { setBulkTagAction('add'); setBulkRemoveTagsSelected([]); setBulkTagSearch(''); }}
              >
                <Ionicons name="add-circle-outline" size={16} color={bulkTagAction === 'add' ? colors.white : colors.primary} />
                <Text style={[styles.bulkTagActionText, bulkTagAction === 'add' && styles.bulkTagActionTextActive]}>{t('contacts.bulkTag.addTags')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.bulkTagActionButton, { flexDirection: rtl.rowDirection }, bulkTagAction === 'remove' && styles.bulkTagActionButtonActiveRemove]}
                onPress={() => { setBulkTagAction('remove'); setBulkTagInput(''); }}
              >
                <Ionicons name="remove-circle-outline" size={16} color={bulkTagAction === 'remove' ? colors.white : colors.error} />
                <Text style={[styles.bulkTagActionText, bulkTagAction === 'remove' && styles.bulkTagActionTextActive]}>{t('contacts.bulkTag.removeTags')}</Text>
              </TouchableOpacity>
            </View>

            {bulkTagAction === 'add' ? (
              <>
                <TextInput
                  style={[styles.bulkTagInput, { textAlign: rtl.textStart }]}
                  placeholder={t('contacts.bulkTag.placeholder')}
                  placeholderTextColor={colors.text.secondary}
                  value={bulkTagInput}
                  onChangeText={setBulkTagInput}
                  multiline
                  editable={!bulkTagging}
                />
                <Text style={[styles.bulkTagHint, { textAlign: rtl.textStart }]}>{t('contacts.bulkTag.hint')}</Text>

                {availableTags.length > 0 && (
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.bulkTagSuggestions}>
                    {availableTags.map(tag => (
                      <TouchableOpacity
                        key={tag}
                        style={styles.bulkTagSuggestionChip}
                        onPress={() => {
                          const existing = bulkTagInput.split(/[,\n]/).map(t => t.trim()).filter(Boolean);
                          if (!existing.includes(tag)) {
                            setBulkTagInput(existing.concat(tag).join(', '));
                          }
                        }}
                      >
                        <Text style={styles.bulkTagSuggestionText}>{tag}</Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                )}
              </>
            ) : (
              <>
                <View style={[styles.tagSearchContainer, { flexDirection: rtl.rowDirection }]}>
                  <Ionicons name="search" size={14} color={colors.text.secondary} />
                  <TextInput
                    style={[styles.tagSearchInput, { textAlign: rtl.textStart }]}
                    placeholder={t('contacts.filter.searchTagsPlaceholder')}
                    placeholderTextColor={colors.text.disabled}
                    value={bulkTagSearch}
                    onChangeText={setBulkTagSearch}
                    autoCapitalize="none"
                    editable={!bulkTagging}
                  />
                  {bulkTagSearch.length > 0 && (
                    <TouchableOpacity onPress={() => setBulkTagSearch('')}>
                      <Ionicons name="close-circle" size={14} color={colors.text.secondary} />
                    </TouchableOpacity>
                  )}
                </View>
                <Text style={[styles.bulkTagHint, { textAlign: rtl.textStart }]}>{t('contacts.bulkTag.removeHint')}</Text>
                <ScrollView style={styles.bulkAssignList}>
                  {availableTags.length === 0 ? (
                    <View style={styles.assignmentMenuEmpty}>
                      <Text style={styles.assignmentMenuEmptyText}>{t('contacts.filter.noTagsMatch')}</Text>
                    </View>
                  ) : (
                    availableTags
                      .filter(tag => tag.toLowerCase().includes(bulkTagSearch.trim().toLowerCase()))
                      .map(tag => {
                        const isSelected = bulkRemoveTagsSelected.includes(tag);
                        return (
                          <TouchableOpacity
                            key={tag}
                            style={[styles.bulkAssignItem, { flexDirection: rtl.rowDirection }, isSelected && styles.channelMenuItemActive]}
                            onPress={() => toggleBulkRemoveTag(tag)}
                            disabled={bulkTagging}
                          >
                            <Ionicons name={isSelected ? 'checkmark-circle' : 'pricetag-outline'} size={16} color={isSelected ? colors.white : colors.primary} />
                            <Text style={[styles.bulkAssignItemText, isSelected && styles.channelMenuItemTextActive]}>{tag}</Text>
                          </TouchableOpacity>
                        );
                      })
                  )}
                </ScrollView>
              </>
            )}

            <View style={[styles.bulkTagButtonsRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={[styles.bulkAssignCancel, { flex: 1, marginTop: 0 }]}
                onPress={() => setBulkTagVisible(false)}
                disabled={bulkTagging}
              >
                <Text style={styles.bulkAssignCancelText}>{t('contacts.bulkTag.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.bulkTagSubmitButton,
                  (bulkTagging || (bulkTagAction === 'remove' ? bulkRemoveTagsSelected.length === 0 : !bulkTagInput.trim())) && styles.bulkTagSubmitButtonDisabled,
                  bulkTagAction === 'remove' && styles.bulkTagSubmitButtonRemove,
                ]}
                onPress={handleBulkTagSubmit}
                disabled={bulkTagging || (bulkTagAction === 'remove' ? bulkRemoveTagsSelected.length === 0 : !bulkTagInput.trim())}
              >
                {bulkTagging ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <Text style={styles.bulkTagSubmitText}>
                    {bulkTagAction === 'add' ? t('contacts.bulkTag.submitAdd') : t('contacts.bulkTag.submitRemove')}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Bulk Data List Membership Modal — add or remove every selected
          contact to/from a system Contacts Data List in one action. Only
          system:'contacts' lists are offered (availableContactDataLists —
          the same set the filter dropdown uses), since linking only makes
          sense for a list a Contact can actually be a member of. */}
      <Modal
        visible={bulkListVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setBulkListVisible(false)}
      >
        <TouchableOpacity
          style={styles.assignmentModalOverlay}
          onPress={() => setBulkListVisible(false)}
          activeOpacity={1}
        >
          <View
            style={styles.bulkAssignModal}
            onStartShouldSetResponder={() => true}
          >
            <Text style={[styles.bulkAssignTitle, { textAlign: rtl.textStart }]}>{t('contacts.bulkList.title')}</Text>
            <Text style={[styles.bulkAssignSubtitle, { textAlign: rtl.textStart }]}>{t('contacts.header.selectedCount', { count: selectedSet.size })}</Text>

            <View style={[styles.bulkTagActionRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={[styles.bulkTagActionButton, { flexDirection: rtl.rowDirection }, bulkListAction === 'add' && styles.bulkTagActionButtonActive]}
                onPress={() => setBulkListAction('add')}
              >
                <Ionicons name="add-circle-outline" size={16} color={bulkListAction === 'add' ? colors.white : colors.primary} />
                <Text style={[styles.bulkTagActionText, bulkListAction === 'add' && styles.bulkTagActionTextActive]}>{t('contacts.bulkList.addTo')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.bulkTagActionButton, { flexDirection: rtl.rowDirection }, bulkListAction === 'remove' && styles.bulkTagActionButtonActiveRemove]}
                onPress={() => setBulkListAction('remove')}
              >
                <Ionicons name="remove-circle-outline" size={16} color={bulkListAction === 'remove' ? colors.white : colors.error} />
                <Text style={[styles.bulkTagActionText, bulkListAction === 'remove' && styles.bulkTagActionTextActive]}>{t('contacts.bulkList.removeFrom')}</Text>
              </TouchableOpacity>
            </View>

            {bulkListLocked ? (
              <View style={{ gap: spacing.sm, marginVertical: spacing.sm }}>
                <Text style={[styles.bulkAssignSubtitle, { textAlign: rtl.textStart }]}>{t('contacts.bulkList.locked')}</Text>
                <TouchableOpacity style={styles.bulkTagSubmitButton} onPress={() => { setBulkListVisible(false); navigateTo('subscription'); }}>
                  <Text style={styles.bulkTagSubmitText}>{t('contacts.bulkList.upgrade')}</Text>
                </TouchableOpacity>
              </View>
            ) : bulkListAction === 'add' ? (
              <View style={[{ flexDirection: rtl.rowDirection, gap: spacing.xs, alignItems: 'center', marginTop: spacing.sm }]}>
                <TextInput
                  value={bulkListNewName}
                  onChangeText={setBulkListNewName}
                  placeholder={t('contacts.bulkList.newPlaceholder')}
                  placeholderTextColor={colors.textLight as string}
                  style={[styles.bulkTagInput, { flex: 1, textAlign: rtl.textStart }]}
                  onSubmitEditing={handleCreateBulkList}
                />
                <TouchableOpacity
                  style={[styles.bulkTagSubmitButton, { flex: 0, paddingHorizontal: spacing.md }, (!bulkListNewName.trim() || bulkListCreating) && styles.bulkTagSubmitButtonDisabled]}
                  onPress={handleCreateBulkList}
                  disabled={!bulkListNewName.trim() || bulkListCreating}
                >
                  {bulkListCreating ? <ActivityIndicator size="small" color={colors.white} /> : <Text style={styles.bulkTagSubmitText}>{t('contacts.bulkList.create')}</Text>}
                </TouchableOpacity>
              </View>
            ) : null}
            {!bulkListLocked && availableContactDataLists.length === 0 ? (
              <Text style={[styles.bulkAssignSubtitle, { textAlign: rtl.textStart }]}>{t('contacts.bulkList.empty')}</Text>
            ) : null}

            <ScrollView style={styles.bulkListPicker}>
              {availableContactDataLists.map(list => (
                <TouchableOpacity
                  key={list._id}
                  style={[styles.bulkListPickerRow, { flexDirection: rtl.rowDirection }, bulkListTargetId === list._id && styles.bulkListPickerRowActive]}
                  onPress={() => setBulkListTargetId(list._id)}
                >
                  <Ionicons name="list-outline" size={18} color={bulkListTargetId === list._id ? colors.white : colors.primary} />
                  <Text style={[styles.bulkListPickerRowText, bulkListTargetId === list._id && styles.bulkListPickerRowTextActive]} numberOfLines={1}>
                    {list.name}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <View style={[styles.bulkTagButtonsRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={[styles.bulkAssignCancel, { flex: 1, marginTop: 0 }]}
                onPress={() => setBulkListVisible(false)}
                disabled={bulkListSaving}
              >
                <Text style={styles.bulkAssignCancelText}>{t('contacts.bulkTag.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.bulkTagSubmitButton,
                  (bulkListSaving || !bulkListTargetId) && styles.bulkTagSubmitButtonDisabled,
                  bulkListAction === 'remove' && styles.bulkTagSubmitButtonRemove,
                ]}
                onPress={handleBulkListSubmit}
                disabled={bulkListSaving || !bulkListTargetId}
              >
                {bulkListSaving ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <Text style={styles.bulkTagSubmitText}>
                    {bulkListAction === 'add' ? t('contacts.bulkList.submitAdd') : t('contacts.bulkList.submitRemove')}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Tags Popover — compact rows hide tags behind an icon; this shows
          the full list, positioned from the touch point and clamped to the
          viewport so it never clips in the narrow right-pane. */}
      <Modal
        transparent
        visible={!!tagsPopoverContact}
        animationType="fade"
        onRequestClose={() => setTagsPopoverContact(null)}
      >
        <TouchableOpacity
          style={styles.modalBackdrop}
          activeOpacity={1}
          onPress={() => setTagsPopoverContact(null)}
        >
          <View
            style={[
              styles.tagsPopoverContainer,
              tagsPopoverPosition
                ? {
                    top: tagsPopoverPosition.y + 8,
                    left: Math.max(spacing.sm, Math.min(tagsPopoverPosition.x - 100, screenWidth - 220)),
                  }
                : { top: 60, right: spacing.lg },
            ]}
          >
            <View style={styles.tagsPopover}>
              <Text style={[styles.tagsPopoverTitle, { textAlign: rtl.textStart }]}>{t('contacts.tags.title')}</Text>
              <View style={[styles.tagsPopoverList, { flexDirection: rtl.rowDirection }]}>
                {tagsPopoverContact?.tags?.map(tag => (
                  <View key={tag} style={styles.contactTagChip}>
                    <Text style={styles.contactTagText} numberOfLines={1}>{tag}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>

    </View>
  );
};

export const ContactsList: React.FC<ContactsListProps> = (props) => (
  props.mode === 'inbox' ? (
    <ContactsFilterScopeContext.Provider value="inbox">
      <ContactsListBody {...props} />
    </ContactsFilterScopeContext.Provider>
  ) : (
    <ContactsListBody {...props} />
  )
);

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
    overflow: 'visible',
  },
  header: {
    flexDirection: 'column',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  headerBare: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderBottomWidth: 0,
    backgroundColor: 'transparent',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerRight: {
    alignItems: 'center',
    rowGap: spacing.xs,
    columnGap: spacing.sm,
  },
  checkboxWrapper: {
    marginRight: spacing.sm,
    justifyContent: 'center',
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.textDark,
    marginLeft: spacing.sm,
  },
  contactCount: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
    marginLeft: spacing.xs,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.secondary,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    marginHorizontal: spacing.lg,
    marginVertical: spacing.md,
    height: 36,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    marginLeft: spacing.sm,
    ...Platform.select({
      web: {
        outlineStyle: 'none' as any,
      },
    }),
  },
  tagSearchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.secondary,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    marginHorizontal: spacing.sm,
    marginTop: spacing.xs,
    marginBottom: spacing.sm,
    height: 32,
    gap: spacing.xs,
  },
  tagSearchInput: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    ...Platform.select({
      web: {
        outlineStyle: 'none' as any,
      },
    }),
  },
  tagSearchEmptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    textAlign: 'center',
    paddingVertical: spacing.md,
  },
  // Bounded, independently-scrollable sub-list for tags/data lists inside
  // the combined Filters menu — a long tag/list list scrolls within this
  // box instead of forcing the whole menu (and everything below it) to
  // scroll past every entry.
  filterSubListContainer: {
    maxHeight: 220,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.sm,
    marginTop: spacing.xs,
    overflow: 'hidden',
  },
  filterSubListScroll: {
    maxHeight: 220,
  },
  channelFilterButton: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 2,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border,
  },
  // Right-pane/slide-over usage (compact) has far less width to spend on
  // the toolbar than the full screen does, so every box shrinks there.
  channelFilterButtonCompact: {
    width: 28,
    height: 28,
  },
  channelFilterButtonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  // All the filter-type buttons (Channel, Assignment, Tags, ...) share one
  // bordered "group" pill instead of each having its own separate bordered
  // box — reads as a single toolbar control and takes a fraction of the
  // space 8 individually-boxed buttons did. Labels were tried here (so each
  // icon would be self-explanatory without already knowing the toolbar by
  // heart) but made the group too wide again; instead each button carries a
  // web hover tooltip (the `title` prop) and an accessibilityLabel, and once
  // tapped, the modal it opens has its own clear title anyway.
  filterChipGroup: {
    alignItems: 'center',
    flexWrap: 'wrap',
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.sm,
    padding: 2,
  },
  filterChip: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 4,
    borderRadius: spacing.borderRadius.sm - 2,
  },
  filterChipCompact: {
    width: 26,
    height: 26,
  },
  filterChipActive: {
    width: undefined,
    paddingHorizontal: spacing.xs,
    backgroundColor: colors.primary,
  },
  filterChipDivider: {
    width: 1,
    height: 18,
    backgroundColor: colors.border,
  },
  filterChipDividerCompact: {
    height: 14,
  },
  channelMenuOverlay: {
    position: 'absolute',
    top: 60,
    right: spacing.lg,
    zIndex: 2000,
    elevation: 50,
    backgroundColor: 'transparent',
  },
  channelMenu: {
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    minWidth: 160,
  },
  menuScroll: {
    flexGrow: 0,
  },
  menuScrollContent: {
    paddingVertical: 4,
  },
  channelMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  channelMenuItemActive: {
    backgroundColor: colors.primary,
  },
  channelMenuItemText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.textDark,
  },
  channelMenuItemTextActive: {
    color: colors.white,
  },
  tagMenuItemExcluded: {
    backgroundColor: withAlpha(colors.error, 0.08),
  },
  tagMenuItemExcludedText: {
    color: colors.error,
  },
  assignmentMenuSection: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.xs,
    marginTop: spacing.xs,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  // Previously fontSize.xs + text.secondary — easy to miss against the
  // list items below it, especially the first couple of sections which
  // didn't even have the divider/section wrapper other sections got.
  // Bigger, bolder, colored, with a top divider for real visual separation.
  assignmentMenuSectionTitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.bold,
    color: colors.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  assignmentMenuLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  assignmentMenuLoadingText: {
    marginLeft: spacing.sm,
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
  assignmentMenuEmpty: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  assignmentMenuEmptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.disabled,
  },
  overlayBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 1999,
    elevation: 49,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  assignmentModalOverlay: {
    flex: 1,
    ...overlayBackdropStyle(),
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  bulkAssignModal: {
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: spacing.borderRadius.md,
    width: '100%',
    maxWidth: 420,
    maxHeight: '80%',
    padding: spacing.lg,
    ...overlayCardLook(),
  },
  bulkAssignTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.textDark,
  },
  bulkAssignSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginTop: spacing.xs,
    marginBottom: spacing.md,
  },
  pageSizeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
  },
  pageSizeInput: {
    width: 64,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    textAlign: 'center',
  },
  bulkAssignModeToggle: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginBottom: spacing.md,
  },
  bulkAssignModeButton: {
    flex: 1,
    paddingVertical: spacing.sm,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: colors.background.secondary,
  },
  bulkAssignModeButtonActive: {
    backgroundColor: colors.primary,
  },
  bulkAssignModeButtonText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  bulkAssignModeButtonTextActive: {
    color: colors.white,
  },
  bulkAssignList: {
    flexGrow: 0,
  },
  bulkAssignItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  bulkAssignItemText: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
  },
  bulkAssignSectionSpacing: {
    marginTop: spacing.md,
  },
  bulkAssignCancel: {
    marginTop: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.background.secondary,
    alignItems: 'center',
  },
  bulkAssignCancelText: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  bulkTagActionRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  bulkTagActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  bulkTagActionButtonActive: {
    backgroundColor: colors.primary,
  },
  bulkTagActionButtonActiveRemove: {
    backgroundColor: colors.error,
    borderColor: colors.error,
  },
  bulkTagActionText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.textDark,
  },
  bulkTagActionTextActive: {
    color: colors.white,
  },
  bulkTagInput: {
    marginTop: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.sm,
    padding: spacing.sm,
    minHeight: 60,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    textAlignVertical: 'top',
  },
  bulkTagHint: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    marginTop: spacing.xs / 2,
  },
  bulkTagSuggestions: {
    marginTop: spacing.sm,
    maxHeight: 36,
  },
  bulkTagSuggestionChip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 999,
    backgroundColor: colors.background.secondary,
    marginRight: spacing.xs,
  },
  bulkTagSuggestionText: {
    fontSize: typography.fontSize.xs,
    color: colors.textDark,
  },
  bulkListPicker: {
    marginTop: spacing.sm,
    maxHeight: 220,
  },
  bulkListPickerRow: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
  },
  bulkListPickerRowActive: {
    backgroundColor: colors.primary,
  },
  bulkListPickerRowText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
  },
  bulkListPickerRowTextActive: {
    color: colors.white,
  },
  locationFilterInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    marginTop: spacing.xs,
  },
  radiusFilterRow: {
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  radiusFilterInput: {
    flex: 1,
    marginTop: 0,
  },
  locationFilterPickerRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    marginTop: spacing.xs,
  },
  locationFilterPickerText: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
  },
  locationFilterPickerPlaceholder: {
    color: colors.text.disabled,
  },
  locationCityList: {
    maxHeight: 160,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.sm,
    marginTop: spacing.xs,
    ...(Platform.OS === 'web' && ({ overflowY: 'auto' } as any)),
  },
  // Plain scrollable View (not RN's ScrollView) for the two lists nested
  // inside the menu's own outer ScrollView — nested ScrollViews fight over
  // touch capture on web (nestedScrollEnabled is Android-only and has no
  // effect there), which was swallowing taps on city/country rows and
  // letting them fall through to the backdrop, closing the whole menu
  // instead of toggling the row.
  locationInnerScrollList: {
    maxHeight: 130,
    ...(Platform.OS === 'web' && ({ overflowY: 'auto' } as any)),
  },
  locationCityItem: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  locationCityItemText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
  },
  locationCityEmptyText: {
    padding: spacing.sm,
    fontSize: typography.fontSize.sm,
    color: colors.text.disabled,
  },
  flagEmoji: {
    fontSize: typography.fontSize.base,
  },
  locationCountryPicker: {
    marginTop: spacing.sm,
  },
  // A plain View, not ScrollView — RN's ScrollView claims touches
  // aggressively for its own gesture responder, which on web swallowed taps
  // on children (the radius TextInput, city/country rows) before they could
  // register, letting them fall through to the modal's backdrop and close
  // it. overflow-y:auto (web) gives the same "scrolls when content is
  // taller than maxHeight" behavior without RN's touch-capture machinery;
  // native doesn't need this menu to scroll internally at this size.
  locationMenuScroll: ({
    padding: spacing.md,
    ...(Platform.OS === 'web' && { overflowY: 'auto' }),
  } as any),
  locationFilterRow: {
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  locationSortButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  locationSortButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
  },
  includeUnlocatedRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.sm,
    paddingVertical: spacing.xs,
  },
  includeUnlocatedText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    marginEnd: spacing.sm,
  },
  locationApplyButton: {
    marginTop: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.primary,
    alignItems: 'center',
  },
  locationApplyButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  bulkTagButtonsRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  bulkTagSubmitButton: {
    flex: 1,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bulkTagSubmitButtonRemove: {
    backgroundColor: colors.error,
  },
  bulkTagSubmitButtonDisabled: {
    opacity: 0.5,
  },
  bulkTagSubmitText: {
    fontSize: typography.fontSize.sm,
    color: colors.white,
    fontFamily: typography.fontFamily.medium,
  },
  menuModalContainer: {
    position: 'absolute',
    top: 60,
    right: spacing.lg,
    left: undefined,
    maxWidth: 280,
  },
  tagsPopoverContainer: {
    position: 'absolute',
    maxWidth: 220,
  },
  tagsPopover: {
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    padding: spacing.sm,
    minWidth: 160,
  },
  tagsPopoverTitle: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    textTransform: 'uppercase',
    marginBottom: spacing.xs,
  },
  tagsPopoverList: {
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  contactsList: {
    flex: 1,
  },
  providerSection: {
    marginBottom: spacing.sm,
  },
  providerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  providerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border,
  },
  providerName: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  providerTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
  },
  providerIcon: {
    marginRight: spacing.xs,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  // The row whose profile/conversation is currently open elsewhere (a
  // screen's middle/right pane) — a left accent bar + tinted background,
  // same primary-color language as checkboxChecked, but visually distinct
  // from it since a row can be active, bulk-selected, both, or neither.
  inboxNameRow: {
    alignItems: 'center',
    gap: 4,
  },
  inboxNameText: {
    flexShrink: 1,
  },
  inboxPreviewRow: {
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  inboxPreviewText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
  inboxPreviewTextUnread: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  inboxMeta: {
    alignItems: 'flex-end',
    gap: 4,
    marginStart: spacing.sm,
  },
  inboxTime: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  inboxUnreadBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 6,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  inboxUnreadText: {
    color: colors.white,
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.bold,
  },
  inboxStatusRow: {
    flexWrap: 'wrap',
    gap: spacing.xs,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm,
  },
  inboxStatusText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    marginStart: 4,
  },
  inboxStatusTextActive: {
    color: colors.white,
  },
  contactItemActive: {
    backgroundColor: withAlpha(colors.primary, 0.08),
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
    paddingLeft: spacing.lg - 3,
  },
  viewContactChevron: {
    marginLeft: spacing.xs,
  },
  contactAvatarContainer: {
    position: 'relative',
    marginRight: spacing.md,
  },
  contactAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.background.secondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactAvatarText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.bold,
    color: colors.primary,
    textAlign: 'center',
    lineHeight: 40,
  },
  statusDot: {
    position: 'absolute',
    bottom: 1,
    right: 1,
    width: 10,
    height: 10,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: colors.background.surface,
  },
  contactInfo: {
    flex: 1,
    minWidth: 0,
    marginRight: spacing.sm,
  },
  contactName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
    marginBottom: spacing.xs,
  },
  assignmentBadgeWrapper: {
    alignSelf: 'flex-start',
  },
  assignmentBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: withAlpha(colors.primary, 0.13), // Light blue background (20% opacity)
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
    borderWidth: 1,
    borderColor: withAlpha(colors.primary, 0.25), // Slightly darker border
  },
  assignmentBadgeUnassigned: {
    backgroundColor: colors.background.secondary,
    borderColor: colors.border,
  },
  assignmentText: {
    fontSize: 11,
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
    letterSpacing: 0.3,
  },
  assignmentTextUnassigned: {
    color: colors.text.disabled,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing.xl,
  },
  refreshingBar: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    backgroundColor: colors.background.surfaceRaised,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  refreshingBarText: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  loadingText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: spacing.sm,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
    minHeight: 300,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginTop: spacing.sm,
    textAlign: 'center',
  },
  emptySubtitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: spacing.sm,
    textAlign: 'center',
    lineHeight: 20,
  },
  loadingMoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
  },
  loadingMoreText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginLeft: spacing.sm,
  },
  contactTagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: 2,
  },
  tagsIconButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 3,
    backgroundColor: withAlpha(colors.primary, 0.09),
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 1,
    marginTop: 2,
  },
  tagsIconCount: {
    fontSize: 10,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  contactTagChip: {
    maxWidth: 140,
    backgroundColor: withAlpha(colors.primary, 0.09),
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 1,
  },
  contactTagText: {
    flexShrink: 1,
    fontSize: 10,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  contactTagMore: {
    fontSize: 10,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
    alignSelf: 'center',
  },
  loadMoreButton: {
    alignItems: 'center',
    paddingVertical: spacing.md,
    marginHorizontal: spacing.lg,
    marginBottom: spacing.lg,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  loadMoreButtonText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
  },
});
